import { LangStore, t, useLang } from '@cortex/i18n';
import { type PrismWebsiteIssue, classifyPrismWebsite } from '@cortex/schemas';
// PrismShell — der "Prism" Trial-Paywall-Funnel als funktionale React-Komponente.
//
// 1:1-Port des Design-Prototyps (prism-trial-paywall.dc.html, class Component
// extends DCLogic, Zeilen 1223–1435) + zugehöriges Markup
// (prism-extracted-markup.html). Standalone, isoliert, ohne Backend —
// "Stufe 1" unter der internen Route /dev/prism-demo.
//
// 8-Phasen-State-Machine:
//   target → website → analyzing → questions → searching → results → paywall
// plus zwei Overlays (Inline-Checkout, Video-Modal), die über die Paywall gelegt
// werden. Alle Timings, Copy und Optik entsprechen dem Prototyp; die einzige
// "echte" Interaktion ist der Fake-Checkout (setTimeout → paid). Das
// Video-Modal zeigt das echte Dub-Video (dasselbe wie die anderen Paywalls).
//
// React-Import bewusst als Namespace (nicht type-only): der Vite-Plugin nutzt
// den classic JSX-Runtime (jsxRuntime: 'classic'), daher muss React zur
// JSX-Transform-Zeit im Scope sein — analog zu PricingDemoPage.tsx.
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import AuthLangSwitch from '../../components/auth/AuthLangSwitch';
import { PRISM_TURNSTILE_CONTAINER_ID, usePrismTurnstile } from '../../hooks/use-prism-turnstile';
import { pushDataLayer } from '../../lib/analytics';
import { REVIEW_PLATFORM_LABEL, selectVerifiedReviews } from '../../lib/verifiedReviews';
import PrismEmbeddedCheckout from './PrismEmbeddedCheckout';
import PrismPaymentElement from './PrismPaymentElement';
import { clearFunnelSnapshot, loadFunnelSnapshot, saveFunnelSnapshot } from './funnelPersistence';
import { type PrismEventFrom, type PrismServerEvent, sendPrismEvent } from './prismEvents';
import './prism.css';
import './prismStage.css';
import './prismResults.css';
import './prismPaywall.css';
import {
  type FunnelQuestion,
  type FunnelStep,
  type Lead,
  analyzeSteps,
  faviconUrl,
  fmtInt,
  initials,
  leadDomain,
  leadScore,
  leadSetFor,
  questions,
  scoreTier,
  searchStepsFor,
  seededMetrics,
} from './fixtures';

// ── Live engine: the funnel POSTs {target, website, ...} to the Prism engine.
// Stufe 2 (LEA-379): these hit the real session-authed API on the SAME origin
// ('/api/prism/*', apps/api → @cortex/scraper engine), not the old :4310 dev
// server. Fetches send credentials:'include' so the cortex_session cookie
// travels. On the authed OnboardingGate path a logged-in user gets REAL data;
// the /dev/prism-demo sandbox is unauthed, so these 401 and the funnel falls
// back to the mock fixtures (kept working). A proper error/retry state for
// authed failures lands in F2 (demo-prop gating).
const PRISM_ANALYZE_URL = '/api/prism/analyze';
const PRISM_SEARCH_URL = '/api/prism/search';
const PRISM_VALIDATE_URL = '/api/prism/validate';
const PRISM_REPRESENT_URL = '/api/prism/represent';
const PRISM_RUN_URL = '/api/prism/run'; // A2: GET /api/prism/run/:id — recover a completed run after a gateway timeout
// 16.10.2026: Angebots-Schalter (wöchentliche Lieferung live?) + Entscheider vor dem Kauf.
const PRISM_CONFIG_URL = '/api/prism/config';
const PRISM_PREVIEW_URL = '/api/prism/preview';
// Der Server bricht die Recherche nach 150 s ab; der Funnel fragt so lange nach.
// Review #961: länger als die rechnerische Höchstdauer am Server (12 Scans in 3 Runden à 45 s,
// Finder, 2 Rückfall-Suchen à 90 s ≈ 6 min): 2 min im 3-s-Takt, danach alle 10 s bis ~10 min.
const TEASER_POLL_MS = 3000;
const TEASER_POLL_FAST = 40;
const TEASER_POLL_SLOW_MS = 10_000;
const TEASER_POLL_MAX = 88;
// F9: Marker in engineErr für den 429-Fall — trennt „Tages-Kontingent" vom
// generischen Fehler-/Refine-Pfad (eigener Screen, kein Run-Poll, keine Chips).
const RATE_LIMIT_SIGNAL = 'rate_limited_429';

// A2 (Janik 07.07.): a /search slower than the ~60s DO/Cloudflare gateway window
// gets 504'd at the client while the backend keeps running and persists the
// result to the prismRun doc. Poll the saved run so a slow-but-successful search
// still reaches the paywall instead of a false "too broad". Polls every 3s up to
// ~36s (covers the backend finishing past the 60s cut). Returns the rebuilt
// EngineResult, or null if it never readies.
async function pollRunResult(prismRunId: string | null): Promise<EngineResult | null> {
  if (!prismRunId) return null;
  for (let i = 0; i < 12; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const res = await fetch(`${PRISM_RUN_URL}/${encodeURIComponent(prismRunId)}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 410) return null; // run expired → give up
        continue;
      }
      const data = (await res.json()) as { ready?: boolean } & EngineResult;
      if (data.ready) return data;
    } catch {
      // network hiccup — keep polling until the loop budget runs out
    }
  }
  return null;
}
// Inline Embedded Checkout (Stufe 4.3) ist OPT-IN — Default = der bewährte,
// von Om verifizierte Redirect-Checkout (Stripe Hosted). Janik 04.07. auf
// staging: der Embed rendert zwar (Session da), ist aber nicht funktionsfähig
// (braucht Domain-Registrierung + Live-Verifikation). Bis das sauber steht,
// bleibt der Embed hinter VITE_PRISM_EMBEDDED_CHECKOUT=true; Launch = Redirect.
const EMBEDDED_CHECKOUT_ENABLED = import.meta.env.VITE_PRISM_EMBEDDED_CHECKOUT === 'true';

// Live-Demo-Video: dasselbe per-locale Dub-Video wie die bestehenden Paywalls
// (OnboardingGate, ids von Janik 10.06). Stufe 1 = Demo → DE als Default; die
// Locale-Auswahl kommt in Stufe 2 mit dem echten Offer. Lazy-Load im Modal,
// Stop beim Schließen via about:blank (1:1-Muster aus OnboardingGate).
const DUB_VIDEO_BASE = 'https://public.deimann.io/dub/v/';
const DUB_VIDEO_DE = 'A3b5ETa6r6jEilxe8otLYn9cmeYTd01AHPkTXwAhrGQI';
// Echtes Poster fürs Demo-Thumbnail: die Dub-ID ist eine Mux-Playback-ID, Mux
// liefert daraus ein echtes Video-Standbild (verifiziert: HTTP 200, image/jpeg).
const DUB_VIDEO_POSTER = `https://image.mux.com/${DUB_VIDEO_DE}/thumbnail.jpg?time=2`;
type EngineCompany = {
  name: string;
  domain: string;
  address?: string;
  fit_reason?: string;
  fit_score?: number;
  // #5 (Welle 2): Engine liefert beide in PrismCompany (desc = Kurzbeschreibung/
  // Kategorie, rating = Serper-Places-Bewertung) — nur der Frontend-Typ hat sie
  // bisher gedroppt. Für die Vault-Wand (Meta-Zeile aus address + desc) nötig.
  desc?: string;
  rating?: number;
  // Engine 2.2 (29.09.2026): wörtlicher Auszug von der Website der Firma, im Seitentext geprüft.
  evidence?: string;
};
// Demo-Pool (#nur /dev/prism-demo): echte-anmutende Firmen, damit die Vault-Wand
// im unauth Sandbox rendert (sonst leer → Wand unsichtbar). NIE im echten Kunden-
// Pfad genutzt (Guard: demo && !engine.poolCompanies). Bewusst variable Namens-
// längen, um die Zeilen-Ausrichtung der Wand realistisch zu prüfen.
// Demo-only (#nur /dev/prism-demo): Suchbegriffe für die „So haben wir gesucht"-Zeile.
const DEMO_QUERIES = [
  'Elektriker Köln',
  'Elektroinstallateur Bonn',
  'Elektrotechnik Leverkusen',
  'Elektrofirma Bergisch Gladbach',
  'Photovoltaik Installateur Köln',
];
const DEMO_POOL_COMPANIES: EngineCompany[] = [
  {
    name: 'Le Werk Kreativagentur',
    domain: 'lewerk.de',
    address: 'Köln, NRW',
    desc: 'Kreativagentur',
    fit_score: 78,
    fit_reason:
      'Kreativagentur in Köln (Rheinland/NRW) trifft die Zielgruppe der Branding- & Designstudios.',
  },
  {
    name: 'zierquadrat',
    domain: 'zierquadrat.de',
    address: 'Düsseldorf, NRW',
    desc: 'Branding-Agentur',
    fit_score: 78,
    fit_reason:
      'Branding-Agentur mit Sitz in Düsseldorf (Rheinland/NRW) – Name und Domain signalisieren Markenarbeit.',
  },
  {
    name: 'SEVN Agentur',
    domain: 'sevn.de',
    address: 'Köln, NRW',
    desc: 'Design & Strategie',
    fit_score: 82,
    fit_reason:
      'Kreativagentur in Köln mit explizitem Schwerpunkt auf Design, Branding und Strategie.',
  },
  {
    name: 'quso. brands',
    domain: 'quso.de',
    address: 'Köln, NRW',
    desc: 'Fullservice Branding',
    fit_score: 78,
    fit_reason:
      'Fullservice Branding-Agentur in Köln (Rheinland/NRW) – passt direkt zur Zielgruppe.',
  },
  {
    name: 'vrej',
    domain: 'vrej.de',
    address: 'Köln, NRW',
    desc: 'Marken- & Designbüro',
    fit_score: 80,
    fit_reason:
      'Marken- und Designbüro in Köln mit Fokus auf strategisches Branding und Markenentwicklung.',
  },
  {
    name: 'ARTENREICH Werbeagentur',
    domain: 'artenreich.de',
    address: 'Köln, NRW',
    desc: 'Werbeagentur',
    fit_score: 78,
    fit_reason:
      'Kreativ- und Werbeagentur aus Köln mit explizitem Branding-Fokus, die direkt der Zielgruppe entspricht.',
  },
  {
    name: 'DISTINCTIVE',
    domain: 'distinctive.de',
    address: 'Düsseldorf, NRW',
    desc: 'Marken- & Kreativagentur',
    fit_score: 76,
    fit_reason:
      'Marken- & Kreativagentur aus Düsseldorf (Rheinland/NRW) – Snippet und Domain deuten auf Markenarbeit.',
  },
  {
    name: 'Belly Creative Studios',
    domain: 'bellycreative.de',
    address: 'Düsseldorf, NRW',
    desc: 'Kreativagentur',
    fit_score: 75,
    fit_reason:
      'Kreativagentur in Düsseldorf (Rheinland/NRW) – Snippet nennt explizit Brand Building als Leistung.',
  },
  {
    name: 'brandcom GmbH',
    domain: 'brandcom.de',
    address: 'Köln, NRW',
    desc: 'Werbeagentur',
    fit_score: 72,
    fit_reason:
      'Werbeagentur mit Sitz in Köln (Rheinland/NRW) passt zur Zielgruppe der Branding-Studios.',
  },
  {
    name: 'T.W.O Kreativagentur GmbH & Co. KG',
    domain: 'two-kreativ.de',
    address: 'Düsseldorf, NRW',
    desc: 'Kreativagentur',
    fit_score: 72,
    fit_reason:
      'Kreativagentur in Düsseldorf (Rheinland/NRW), die direkt zur gesuchten Zielgruppe passt.',
  },
  {
    name: 'Rue Design',
    domain: 'ruedesign.de',
    address: 'Köln, NRW',
    desc: 'Designstudio',
    fit_score: 70,
    fit_reason: 'Designstudio in Köln mit Markenfokus – passt zur Zielgruppe.',
  },
  {
    name: 'Formfeld Studio',
    domain: 'formfeld.de',
    address: 'Bonn, NRW',
    desc: 'Designstudio',
    fit_score: 74,
    fit_reason: 'Designstudio in Bonn (Rheinland/NRW) mit Corporate-Design-Schwerpunkt.',
  },
  {
    name: 'Nordkap Markenagentur',
    domain: 'nordkap-marken.de',
    address: 'Essen, NRW',
    desc: 'Markenagentur',
    fit_score: 73,
    fit_reason: 'Markenagentur in Essen (Ruhrgebiet/NRW) – Positionierung und Markenstrategie.',
  },
  {
    name: 'Hej',
    domain: 'hej.de',
    address: 'Köln, NRW',
    desc: 'Kreativagentur',
    fit_score: 71,
    fit_reason: 'Kompakte Kreativagentur in Köln – Branding und Kampagnen.',
  },
  {
    name: 'Kontrast Kommunikation GmbH',
    domain: 'kontrast-kom.de',
    address: 'Düsseldorf, NRW',
    desc: 'Kommunikationsagentur',
    fit_score: 70,
    fit_reason: 'Kommunikationsagentur in Düsseldorf mit Marken- und Designleistungen.',
  },
  {
    name: 'Studio Aer',
    domain: 'studioaer.de',
    address: 'Köln, NRW',
    desc: 'Brand & Digital',
    fit_score: 70,
    fit_reason: 'Brand- & Digital-Studio in Köln – passt zur gesuchten Zielgruppe.',
  },
];
type PrismIcpView = {
  business_model?: string;
  offerings?: string[];
  ideal_customers?: string[];
  geo?: string;
  opportunities?: string[];
  // #1 (Welle 2): EIN „Warum jetzt"-Satz, vom Engine-Agent aus Angebot/Markt-
  // Logik abgeleitet (streng website-gegroundet). Defensiv optional.
  momentum?: string;
  // DEPRECATED (Welle 2): wird nicht mehr erzeugt/gerendert; nur für Alt-Läufe.
  risks?: string[];
  __error?: string;
};
type EngineResult = {
  icp?: PrismIcpView | null;
  questions?: string[];
  companies?: EngineCompany[];
  // #5 (Welle 2): voller gescorter/verifizierter Pool (Superset der Anzeige) —
  // real im /search-Response (engine.ts → API), nur im Frontend-Typ gedroppt.
  poolCompanies?: EngineCompany[];
  discovery?: { kept: number; rawFound: number; droppedDirs: number; queries?: string[] };
  market?: { matched_now?: number; projected_range_dach?: string; disclaimer?: string };
  quality?: {
    onTargetCount: number;
    avgFit: number;
    strong: boolean;
    reason: string;
    // Wie viele Firmen der Kauf aus diesem Lauf liefert (on-target Pool, höchstens 50).
    // Anders als onTargetCount nicht auf die angezeigte Liste begrenzt. Fehlt bei älterer API.
    deliverableCount?: number;
    // Refine-Rettung (Janik 03.07.): konkrete klickbare Alternativ-Zielgruppen.
    suggestions?: string[];
  };
  error?: string;
  prismRunId?: string;
};
// Ergebnis der schnellen Analyse-Phase (P1 + Fragen), noch VOR der Suche.
// questions: die Engine liefert {q, options} (Antwort-Chips, Janik 03.07.);
// reine Strings bleiben als Fallback erlaubt (alter Server/Cache) → Ja/Nein.
type AnalyzeResult = {
  icp?: EngineResult['icp'];
  questions?: Array<string | FunnelQuestion>;
  domainOk?: boolean | null;
  error?: string;
  prismRunId?: string;
};

// Antwort von POST /api/prism/represent — nur die neu-betexteten Engine-Felder
// (Scores/strong bleiben eingefroren, daher hier nicht enthalten).
type RepresentResult = {
  questions?: FunnelQuestion[];
  icp?: EngineResult['icp'];
  companies?: Array<{ domain: string; fit_reason?: string }>;
  suggestions?: string[];
  market?: EngineResult['market'];
};

// GET /api/prism/config. Ohne Antwort (Demo, alte API) gilt: Lieferung aus, Starter 250 Credits.
type PrismConfig = {
  deliveryEnabled: boolean;
  weeklyCompanies: number;
  starterCreditsPerMonth: number;
};
const DEFAULT_PRISM_CONFIG: PrismConfig = {
  deliveryEnabled: false,
  weeklyCompanies: 31,
  starterCreditsPerMonth: 250,
};

// Entscheider vor dem Kauf (16.10.2026), Form von publicTeaser() in apps/api. Die E-Mail kommt
// nur maskiert an.
type TeaserView = {
  status: 'running' | 'found' | 'none' | 'failed' | 'skipped';
  domain: string;
  companyName: string;
  person?: {
    name: string;
    role: string | null;
    source: 'impressum' | 'handelsregister' | 'website';
    sourceUrl: string | null;
    emailMasked: string | null;
    emailStatus: 'verified' | 'probable' | 'found' | 'none';
  } | null;
};
type TargetNotice = 'local_b2c' | 'outside_dach';
// Vorab-Vorschau (16.10.2026), Form von publicPreview() in apps/api. Klartext-E-Mail und Telefon
// kommen nur für Gratis-Leads (kind 'free').
type PreviewCompanyView = {
  domain: string;
  companyName: string;
  kind: 'free' | 'teaser' | 'hint';
  status: 'found' | 'none' | 'failed';
  person?:
    | (NonNullable<TeaserView['person']> & { email: string | null; phone: string | null })
    | null;
  decisionMakers?: number;
  roles?: string[];
  emailPattern?: string | null;
  sources?: Array<'impressum' | 'handelsregister' | 'website'>;
};
type PreviewView = {
  status: 'running' | 'done' | 'skipped';
  freeTarget: number;
  companies: PreviewCompanyView[];
  reason?: string | null;
  /** Nur clientseitig: Nachfragen aufgegeben, obwohl der Server noch 'running' meldete. */
  stalled?: boolean;
};

const normDomain = (d?: string) => (d || '').toLowerCase().replace(/^www\./, '');

/**
 * Abo klar erklärt (16.10.2026): steht neben jedem Kauf-Knopf, im Bezahlfenster und auf der
 * Ergebnisseite. Der Satz zur wöchentlichen Lieferung erscheint NUR, wenn sie live ist
 * (DELIVERY_ENABLED über /api/prism/config); sonst der heutige Satz mit Credits pro Monat.
 */
function AboTerms({ cfg, className = '' }: { cfg: PrismConfig; className?: string }) {
  return (
    <div className={`pz-abo ${className}`.trim()}>
      <div className="pz-abo-line">{t('prism.abo.line')}</div>
      <div className="pz-abo-after">
        {cfg.deliveryEnabled
          ? t('prism.abo.afterDelivery', { n: cfg.weeklyCompanies })
          : t('prism.abo.afterCredits', { credits: cfg.starterCreditsPerMonth })}
      </div>
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .2s' }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

// Struktureller Domain-Check OHNE AI (instant) — fängt Nicht-Domains ab, bevor
// wir überhaupt versuchen, die Seite zu crawlen.
function isPlausibleDomain(input: string): boolean {
  const s = input
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/\s?#]/)[0];
  if (!s || /\s/.test(s)) return false;
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/i.test(
    s,
  );
}

// Fit-Begründung fürs UI auf ~90 Zeichen kürzen (an Wortgrenze, mit „…") —
// die Engine liefert teils lange Sätze, die Lead-Zeilen sind einzeilig.
/**
 * Beleg von der Website der Firma (Engine 2.2): wörtlicher Auszug, den die Engine gegen den
 * geladenen Seitentext geprüft hat. `<q>` setzt die Anführungszeichen der Seitensprache.
 */
function LeadEvidence({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <div className="lead-ev">
      <span className="lead-ev-tag">{t('prism.lead.evidenceTag')}</span> <q>{text}</q>
    </div>
  );
}

function shortReason(s: string, max = 90): string {
  const t = s.trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 60))} …`;
}

const { useMemo } = React;

// ─── State-Typen ──────────────────────────────────────────────────────────

type Phase = 'target' | 'website' | 'analyzing' | 'questions' | 'searching' | 'results' | 'paywall';

interface Plan {
  name: string;
  price: number;
}

const ANALYZE_PER = 900;

// ─── Kleine Präsentations-Bausteine ──────────────────────────────────────

/** Häkchen-Icon (checkmark) — mehrfach im Markup verwendet. */
function CheckIcon({ size = 11, sw = 3 }: { size?: number; sw?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Pfeil-nach-rechts-Icon für CTAs. */
function ArrowIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

/**
 * Leerzeichen zwischen zwei Textteilen aus Locale-Keys: kein Leerzeichen vor Satzzeichen
 * (Keys wie „, 50 leads …“ oder „: 50 Leads …“ beginnen selbst mit dem Zeichen).
 */
function sp(next: string): string {
  return /^[,.:;!?…]/.test(next) ? '' : ' ';
}

/** Uhr-Icon. */
function ClockIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

/** Gift/Paket-Icon (Frei-Lead, Eyebrow). */
function GiftIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" />
    </svg>
  );
}

/** Blitz-/Bolt-Icon. */
function BoltIcon({ size = 14 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

/** Rendert eine Lade-Schritt-Liste (Analyze/Search): erledigte Schritte + aktueller. */
/** Vier Stationen des Funnels; 'analyzing' zählt noch zur Website-Station (die Seite wird gelesen). */
const RAIL_STEPS = ['audience', 'website', 'refine', 'result'] as const;
function railIndex(phase: Phase): number {
  switch (phase) {
    case 'target':
      return 0;
    case 'website':
    case 'analyzing':
      return 1;
    case 'questions':
      return 2;
    case 'searching':
      return 3;
    default:
      return 4; // Ergebnis/Handoff: alles erledigt
  }
}

/** Fortschritt in der Kopfzeile. Ersetzt die Zeile „Schritt X von 3 · …“ über jeder Überschrift. */
function PrismRail({ phase }: { phase: Phase }) {
  const cur = railIndex(phase);
  return (
    // biome-ignore lint/a11y/useSemanticElements: list-style:none nimmt dem <ol> in Safari/VoiceOver die Listensemantik
    // biome-ignore lint/a11y/noRedundantRoles: dieselbe Begründung, die Rolle stellt die Semantik wieder her
    <ol className="pz-rail" role="list" aria-label={t('prism.rail.label')}>
      {RAIL_STEPS.map((key, i) => (
        <li
          key={key}
          className={`pz-rail-step${i < cur ? ' is-done' : ''}${i === cur ? ' is-cur' : ''}`}
          aria-current={i === cur ? 'step' : undefined}
        >
          <span className="pz-rail-dot" aria-hidden="true">
            {i < cur ? <CheckIcon size={10} sw={3.4} /> : i + 1}
          </span>
          <span className="pz-rail-lb">{t(`prism.rail.${key}`)}</span>
          {i < cur && <span className="pz-sr">, {t('prism.rail.done')}</span>}
        </li>
      ))}
    </ol>
  );
}

/** Was in der Adressleiste der Lese-Szene steht: die eingegebene Domain, sonst die Zielgruppe. */
function readingLabel(website: string, target: string): string {
  const host = website
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/\s?#]/)[0];
  return host || target.trim();
}

function StepList({
  doneSteps,
  loadMsg,
  maxDone,
}: {
  doneSteps: FunnelStep[];
  loadMsg: string;
  /** Nur die letzten N erledigten Schritte zeigen, ältere als eine Zeile zusammenfassen. */
  maxDone?: number;
}) {
  // Eine Zeile „N frühere Schritte erledigt“ statt eines Einzelschritts, der sonst nur die Zeile tauscht.
  const hidden =
    maxDone !== undefined && doneSteps.length - maxDone >= 2 ? doneSteps.length - maxDone : 0;
  const shown = hidden ? doneSteps.slice(hidden) : doneSteps;
  return (
    <div className="wu-acts">
      {hidden > 0 && (
        <div className="wu-act wu-act-more">
          <span className="ck">
            <CheckIcon />
          </span>
          <span className="at">{t('prism.steps.earlier', { n: hidden })}</span>
        </div>
      )}
      {shown.map((st) => (
        <div className="wu-act" key={st.t}>
          <span className="ck">
            <CheckIcon />
          </span>
          <span className="at">{st.t}</span>
          {st.n && <span className="an">{st.n}</span>}
        </div>
      ))}
      {loadMsg && (
        <div className="wu-act now">
          <span className="sp" />
          <span className="at">{loadMsg}</span>
        </div>
      )}
    </div>
  );
}

// Echtes Gründer-Foto — dasselbe Asset wie im OnboardingGate (apps/web/public/img/).
const FOUNDER_PHOTO = '/img/janik-founder.jpg';

// Kundenstimmen im Funnel kommen seit 17.09.2026 ausschliesslich aus
// lib/verifiedReviews — dieselbe Regel wie auf der Paywall (PR #751).
// Vorher standen hier vier fest verdrahtete Stimmen unter "Echte Bewertungen von
// zufriedenen Nutzern": deutsche Zitate in JEDER Sprache, und die Gesichter
// waren Repo-Portraets, die nur "gender-passend zu den Namen" zugeordnet waren —
// ein echter Name neben einem fremden Gesicht. Dieselben drei Personen hat #751
// von der Paywall genommen; hier blieben sie stehen.
// Sind die Stimmen echt (Janik 10.07.), gehoeren sie mit Quell-Link in
// VERIFIED_REVIEWS — dann erscheinen sie im Funnel UND auf der Paywall wieder,
// mit Initialen statt geliehener Gesichter, solange keine Foto-Einwilligung
// (consentRef) dokumentiert ist.
function PrismReviewsBand() {
  const reviews = selectVerifiedReviews(4);
  if (reviews.length === 0) return null;
  const platforms = Array.from(new Set(reviews.map((r) => r.platform)));
  return (
    <div className="reviews-band">
      <div className="reviews-band-inner">
        <div className="proof-head">
          <h3>{t('prism.reviews.title')}</h3>
          <span className="review-pill">
            {t('og.trust.proof.pillVerified', {
              platform: platforms.map((p) => REVIEW_PLATFORM_LABEL[p]).join(' & '),
            })}
          </span>
        </div>
        <div className="tstm-grid">
          {reviews.map((r) => (
            <div className="tstm" key={r.id}>
              <div className="t-stars" role="img" aria-label={`${r.rating}/5`}>
                {'★'.repeat(r.rating)}
              </div>
              <div className="t-quote">{r.text}</div>
              <div className="t-who">
                {r.author.photo && r.author.consentRef ? <img alt="" src={r.author.photo} /> : null}
                <div>
                  <div className="t-name">{r.author.name}</div>
                  {r.author.role ? <div className="t-role">{r.author.role}</div> : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Lead-Badges (Favicon + Fit-Score) ─────────────────────────────────────
//
// Favicon lädt echt vom Firmen-Host (DuckDuckGo-Proxy) — lädt es, ist die Firma
// real (netter Realitäts-Check). Fällt es aus, greift das gebrandete Monogramm.
// In Stufe 2 kommen `domain`/`score` verifiziert aus der Pipeline; diese
// Komponenten bleiben unverändert.
function LeadFavicon({ lead, size = 40 }: { lead: Lead; size?: number }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="lead-mono" style={{ width: size, height: size }} aria-hidden="true">
        {lead.in}
      </span>
    );
  }
  return (
    <span className="lead-fav" style={{ width: size, height: size }} aria-hidden="true">
      <img
        src={faviconUrl(leadDomain(lead))}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

// ── Ansprechpartner-Avatare (Janik 03.07.) ────────────────────────────────
// 49 AI-generierte Business-Portraits (public/img/prism/avatars/a1-49.jpg, aus
// Janiks Collage geschnitten). Auswahl deterministisch je Firma (Namens-Hash):
// stabil über Re-Renders, keine Dubletten im Stack. Bewusst OHNE Namen.
// Default DEZENT weichgezeichnet: die Paywall verspricht echte verifizierte
// Ansprechpartner — gestochen scharfe Stock-Gesichter direkt neben realen
// Firmennamen könnten als „das sind DIE Entscheider" gelesen werden (§5-UWG-
// Logik, vgl. entfernte seniorgrade-Fake-Reviews). Scharf = AVATAR_SHARP=true.
// Weich = vorgeblurrte Dateien a{n}-soft.jpg (σ 5,7 px auf 96 px = die frühere
// CSS-Unschärfe von 1,3 px bei 22 px), KEIN filter: blur() mehr: der malte in
// iOS Safari im Ergebnis-Layout eine pinke Linie über jede Zeile (Om, 01.10.).
const AVATAR_COUNT = 49;
const AVATAR_SHARP = false;
function avatarIdsFor(seed: string): number[] {
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  const ids: number[] = [];
  let x = Math.abs(h);
  while (ids.length < 3) {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    const id = (x % AVATAR_COUNT) + 1;
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}
function ApStack({ seed }: { seed: string }) {
  const ids = avatarIdsFor(seed);
  return (
    <span className={AVATAR_SHARP ? 'wu-apstack' : 'wu-apstack soft'} aria-hidden="true">
      {ids.map((id) => (
        <img
          key={id}
          src={`/img/prism/avatars/a${id}${AVATAR_SHARP ? '' : '-soft'}.jpg`}
          alt=""
          width={22}
          height={22}
          loading="lazy"
        />
      ))}
    </span>
  );
}

function ScoreBadge({ lead, top = false }: { lead: Lead; top?: boolean }) {
  const s = lead.score ?? leadScore(lead, top);
  const tier = scoreTier(s);
  return (
    <span className={`lead-score ${tier.cls}`} title={t('prism.score.titleAttr', { score: s })}>
      <span className="ls-num">{s}</span>
      <span className="ls-lbl">{tier.label}</span>
    </span>
  );
}

// ─── Komponente ───────────────────────────────────────────────────────────

export function PrismShell({ demo = false }: { demo?: boolean }) {
  // demo=true → /dev/prism-demo (unauth Sandbox): Mock-Fallback bleibt an, damit
  // die Standalone-Demo auch ohne erreichbare Engine läuft. demo=false → echter
  // Kunden-Pfad (OnboardingGate, eingeloggt): NIE stumm auf Mock-Daten
  // zurückfallen — ein zahlender User darf keine Fake-Leads sehen (F2, LEA-379).
  // Sprach-Reaktivität: re-rendert die Komponente beim Sprachwechsel, damit
  // alle t()-Aufrufe (auch in den fixtures-Buildern) frisch übersetzen.
  const lang = useLang();
  // A2 (LEA-379): invisible Turnstile — getTurnstileToken() mints a fresh single-
  // use token right before /analyze + /search (dark until the VITE_ env is set).
  const { enabled: turnstileEnabled, getToken: getTurnstileToken } = usePrismTurnstile();
  const [phase, setPhase] = useState<Phase>('target');
  const [query, setQuery] = useState('');
  const [website, setWebsite] = useState('');
  const [qStep, setQStep] = useState(0);
  // Gewählte Antworten der Fragen-Phase — gehen als qa an /search (steuern
  // Query-Planung + Fit) und in die Blackbox-Persistenz. Ref statt State:
  // kein Re-Render nötig, gelesen erst beim Suche-Feuern.
  const qaRef = useRef<{ question: string; answer: string }[]>([]);
  const [count, setCount] = useState(0);
  const [loadMsg, setLoadMsg] = useState('');
  const [doneSteps, setDoneSteps] = useState<FunnelStep[]>([]);
  // Premium-Polish (A4-6): kurzer „registriert"-Beat auf der gewählten Antwort,
  // bevor der Flow advancet. pickingRef entprellt Doppel-Klick/Key-Repeat.
  const [picked, setPicked] = useState<string | null>(null);
  const pickingRef = useRef(false);
  // Richtung des Fragen-Wechsels (A4-7): 1 = vorwärts (von rechts), -1 = zurück
  // (von links). Steuert nur die Einlauf-Richtung der Options-Karten.
  const [qDir, setQDir] = useState<1 | -1>(1);
  // #3 (Welle 2): bewusster Zahlen-Morph beim Reveal Such→Ergebnis. Statt hart
  // auf die kleine echte Zahl zu springen (alter Bug), morpht der zuletzt
  // gezeigte Such-Zähler (≈ discovery.rawFound) sichtbar auf discovery.kept.
  // null = Morph noch nicht gestartet → Headline liest den echten kept-Wert.
  const [morphCount, setMorphCount] = useState<number | null>(null);
  const morphRafRef = useRef(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  // Zeitpunkt des letzten Öffnens des Bezahlfensters (Schutz gegen Doppelklick, s. onCoClick).
  const coOpenedAtRef = useRef(0);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  // Stufe 4.3: try inline (embedded) checkout first; if the embed can't init
  // (backend route not ready / Stripe.js blocked), fall back to the redirect CTA.
  const [embedFallback, setEmbedFallback] = useState(false);
  // Stufe 4 (LEA-379): the CUSTOM Payment-Element checkout with our own honest summary
  // takes precedence over the embedded Checkout. Its mount-probe calls onFallback when
  // element mode is dark (or init fails) → we drop to the embedded/redirect chain below.
  const [elementFallback, setElementFallback] = useState(false);
  // Re-present: läuft ein Sprachwechsel MITTEN im Funnel → die schon geholten
  // Engine-Texte in der neuen Sprache neu betexten (Janik 04.07.). true = die
  // betroffenen Blöcke zeigen kurz einen dezenten „…"-Zustand.
  const [representing, setRepresenting] = useState(false);
  // Analyse-Ergebnis (P1 + echte Fragen) — schnell, vor der Fragen-Phase.
  const [analyze, setAnalyze] = useState<AnalyzeResult | null>(null);
  const analyzeRef = useRef<AnalyzeResult | null>(null);
  analyzeRef.current = analyze;
  const analyzeFiredRef = useRef(false);
  // Such-Ergebnis (echte Firmen/Markt/Qualität) — langsam, während der Suche.
  const [engine, setEngine] = useState<EngineResult | null>(null);
  const [engineErr, setEngineErr] = useState<string | null>(null);
  // F9: 429 = Tages-Kontingent erschöpft, KEIN Qualitätsproblem — eigener
  // ehrlicher Zustand statt des „präzisieren"-Screens (der dem Nutzer fälschlich
  // seine Zielgruppe anlastet und dessen Chips nur den nächsten 429 erzeugen).
  // resetAtMs kommt aus X-RateLimit-Reset (Epoch-Sekunden des Fensterendes) —
  // Retry-After wäre die volle 24h-Fensterlänge, also die falsche Zahl.
  const [rateLimitResetMs, setRateLimitResetMs] = useState<number | null>(null);
  const searchFiredRef = useRef(false);
  // LEA-379 funnel analytics (deliverable #6) — one-shot guards so the pre-/analyze
  // events fire once each (lang re-renders / phase re-entry must not double-count).
  const funnelViewedRef = useRef(false);
  const targetTypedRef = useRef(false);
  const stepsSeenRef = useRef<Set<Phase>>(new Set());
  // Refs, damit die setTimeout-Ketten den AKTUELLEN Status live lesen (auf das
  // echte Ergebnis warten statt blind weiterzuanimieren).
  const engineRef = useRef<EngineResult | null>(null);
  engineRef.current = engine;
  const engineErrRef = useRef<string | null>(null);
  engineErrRef.current = engineErr;
  // P3 (LEA-379): prismRunId aus /analyze → an /search durchgereicht (ein Doc pro
  // Lauf) und später an den Checkout, damit der Kauf-Webhook das Konto aus genau
  // diesem Funnel-Lauf seedet (S4).
  const prismRunIdRef = useRef<string | null>(null);
  // F6: Zieltext, auf dem der gefeuerte /analyze basiert. Zurück-Navigation +
  // geänderter Text → invalidateStaleAnalyze() erzwingt einen frischen Lauf,
  // statt Fragen/ICP/Suche stillschweigend auf dem alten Text weiterlaufen zu
  // lassen. Unveränderter Text behält den Cache (kein zweiter billable /analyze).
  const analyzedQueryRef = useRef<string | null>(null);
  // D2 (LEA-430 #3, Server-Seite): fertiger, ungekaufter Lauf vom Server
  // (/run/latest) → die „Willkommen zurück"-Leiste bietet ihn EXPLIZIT an (kein
  // Auto-Jump). Ergänzt Shankys sessionStorage-Snapshot (funnelPersistence.ts):
  // der trägt den GLEICHEN Tab über Refresh/Back, dieser Pfad holt den Lauf
  // TAGE später / in neuem Tab / auf anderem Gerät zurück (Server-TTL 7 Tage).
  const [resumeOffer, setResumeOffer] = useState<
    ({ target?: string; createdAt?: number } & EngineResult) | null
  >(null);
  // Server-resumter Lauf (Willkommen-zurück / Stripe-Cancel): die Paywall zeigt
  // dort die ruhige „Auswahl gespeichert"-Leiste. Einen Countdown gibt es nicht
  // mehr (29.09.2026: nichts wurde reserviert, er war rein clientseitig).
  const [runResumed, setRunResumed] = useState(false);
  // Eingangs-Validierung der Zielgruppe (fail-fast, bevor der teure Lauf startet).
  const [validating, setValidating] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [targetSuggestion, setTargetSuggestion] = useState('');
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  // 16.10.2026: Website-Feld fängt Nicht-Firmen-Adressen ab (leadscraper.de, KI-Chat, Social,
  // Weiterleitung) — freundlicher Hinweis, Überspringen bleibt möglich.
  const [websiteIssue, setWebsiteIssue] = useState<PrismWebsiteIssue | null>(null);
  // Ehrlicher Hinweis zur Datenlage (lokales B2C, außerhalb DACH) — keine Sperre.
  const [targetNotice, setTargetNotice] = useState<TargetNotice | null>(null);
  const [cfg, setCfg] = useState<PrismConfig>(DEFAULT_PRISM_CONFIG);
  // Entscheider vor dem Kauf für die stärkste Firma (Karte aufklappbar).
  const [preview, setPreview] = useState<PreviewView | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [teaserOpen, setTeaserOpen] = useState(false);
  const teaserRunRef = useRef<string | null>(null);
  const teaserEventRef = useRef<string | null>(null);
  const teaserOpenedRef = useRef(false);
  const teaserBlockRef = useRef<HTMLDivElement | null>(null);
  // Klick auf eine andere Firmenkarte: Hinweis statt toter Klick (Clarity 01.–15.10.).
  const [hintFor, setHintFor] = useState<string | null>(null);
  const hintSeenRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );
  // Fixe Kauf-Leiste (Paywall): beobachtet die beiden Kauf-CTAs (Tresor + Plan).
  // Erst wenn KEINER mehr im Viewport ist, blendet die Leiste unten ein — so ist
  // der Kauf-CTA am Desktop beim Scrollen immer erreichbar.
  const vaultCtaRef = useRef<HTMLButtonElement | null>(null);
  const planCtaRef = useRef<HTMLButtonElement | null>(null);
  const [ctaOffscreen, setCtaOffscreen] = useState(false);

  // Phase-Ref: die setTimeout-Ketten prüfen gegen die AKTUELLE Phase (wie im
  // Original `if (this.state.phase !== 'searching') return`) — nicht gegen den
  // eingefrorenen Closure-Wert.
  const phaseRef = useRef<Phase>(phase);
  phaseRef.current = phase;

  // ── LEA-379 funnel analytics (deliverable #6): the PRE-/analyze drop-off the
  // server-side prismRuns log can't see (the run doc is created only at /analyze).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once (funnel top denominator + abandon listener); `demo` is a stable prop.
  useEffect(() => {
    if (funnelViewedRef.current) return;
    funnelViewedRef.current = true;
    // Review LEA-430: ein Refresh, der aus dem Snapshot restauriert, ist KEIN
    // neuer Funnel-View — sonst zählt jeder Reload das Top-of-Funnel-Denominator
    // hoch und verfälscht die Drop-off-Rate. Restaurierte Sessions als eigenes
    // Event loggen (unterscheidbar), statt prism_funnel_viewed zu doppeln.
    const restored = !demo && !!loadFunnelSnapshot();
    if (restored) {
      pushDataLayer('prism_funnel_restored', { lang: LangStore.get() });
    } else {
      pushDataLayer('prism_funnel_viewed', { lang: LangStore.get(), demo: !!demo });
    }
    const onLeave = () => pushDataLayer('prism_abandon', { lastPhase: phaseRef.current });
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, []);
  // LEA-430 #2: Refresh-Persistenz — den letzten stabilen Funnel-Stand aus
  // sessionStorage restaurieren (Om: Refresh am Payment-Step warf auf Schritt 1
  // zurück = verlorener Kauf). Deklariert VOR dem resumeRun-Effekt: trägt der
  // Snapshot bereits denselben Lauf wie ?resumeRun=, übernimmt er (reicher:
  // Paywall + offenes Checkout + laufende Deadline statt nur Ergebnisseite) und
  // der Server-Fetch unten entfällt. Die Fired-Refs werden mitgesetzt, damit
  // nach dem Restore kein zweiter (kostenpflichtiger) /analyze- oder /search-
  // Lauf feuert. Details/Format: funnelPersistence.ts.
  const snapshotRestoredRunRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once restore; `demo` ist eine stabile Prop.
  useEffect(() => {
    if (demo) return;
    const snap = loadFunnelSnapshot();
    if (!snap) return;
    const resumeParam = new URLSearchParams(window.location.search).get('resumeRun');
    // Fremder ?resumeRun= (anderer Lauf als der Snapshot) → Server-Resume gewinnt.
    if (resumeParam && resumeParam !== snap.prismRunId) {
      clearFunnelSnapshot();
      return;
    }
    setQuery(snap.query);
    setWebsite(snap.website);
    qaRef.current = snap.qa;
    setQStep(Math.max(0, snap.qStep));
    if (snap.analyze) {
      const a = snap.analyze as AnalyzeResult;
      analyzeRef.current = a;
      setAnalyze(a);
      analyzeFiredRef.current = true;
    }
    // Review LEA-430: die prismRunId NUR zusammen mit einem fertigen Such-Ergebnis
    // restaurieren. Ohne engine (Refresh in target/website/questions) würde ein
    // erneuter Suchlauf mit derselben Run-Id gegen den noch serverseitig laufenden
    // ersten Lauf rennen (additive Kosten + last-writer-wins auf demselben Doc).
    // Ohne Run-Id mintet /analyze ein frisches Doc — das alte Verhalten, kein Race.
    if (snap.engine) {
      const e = snap.engine as EngineResult;
      engineRef.current = e;
      setEngine(e);
      searchFiredRef.current = true;
      prismRunIdRef.current = snap.prismRunId;
      snapshotRestoredRunRef.current = snap.prismRunId;
    }
    setPhase(snap.phase);
    phaseRef.current = snap.phase;
    // Review LEA-430: der step-viewed-Effekt läuft im ERSTEN Commit noch mit
    // phase='target' (setPhase greift erst in Commit 2) → ohne diese Vorbelegung
    // loggt jeder restaurierte Mount einen Phantom-Step 'target', der die
    // Per-Step-Drop-off-Zahlen verfälscht. 'target' als gesehen markieren; den
    // echten restaurierten Step loggt der Effekt in Commit 2 korrekt nach.
    stepsSeenRef.current.add('target');
    // plan ≠ null öffnet das Checkout-Overlay wieder — der Payment-Step selbst
    // restauriert seine Rechnungsfelder aus dem eigenen Billing-Snapshot.
    if (snap.plan) setPlan(snap.plan);
  }, []);
  // D (Janik 07.07.): Rückkehr vom Stripe-Cancel. Der Prism-Checkout setzt als
  // cancel_url `?resumeRun=<id>` → hier den gespeicherten Lauf holen und direkt auf
  // die Ergebnisseite (Paywall) springen, statt den Funnel neu zu starten. Läuft
  // genau einmal beim Mount; ohne Param passiert nichts. URL wird danach bereinigt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once resume.
  useEffect(() => {
    if (demo) return;
    const runId = new URLSearchParams(window.location.search).get('resumeRun');
    if (!runId) return;
    // LEA-430 #2: derselbe Lauf wurde schon (reicher) aus dem Snapshot restauriert
    // → nur noch die URL bereinigen, kein Server-Fetch und kein Phase-Downgrade
    // auf 'results'.
    if (snapshotRestoredRunRef.current === runId) {
      const url = new URL(window.location.href);
      url.searchParams.delete('resumeRun');
      window.history.replaceState({}, '', url.toString());
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`${PRISM_RUN_URL}/${encodeURIComponent(runId)}`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = (await res.json()) as { ready?: boolean; target?: string } & EngineResult;
        if (!alive || !data.ready || !data.companies?.length) return;
        if (data.target) setQuery(data.target);
        prismRunIdRef.current = data.prismRunId ?? runId;
        searchFiredRef.current = true; // kein erneuter Suchlauf beim Resume
        setRunResumed(true);
        setEngine(data);
        setPhase('results');
        phaseRef.current = 'results';
      } catch {
        // Resume ist best-effort — schlägt es fehl, bleibt der normale Funnel-Start.
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete('resumeRun');
        window.history.replaceState({}, '', url.toString());
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  // LEA-430 #2: jeden stabilen Stand nach sessionStorage spiegeln. Transiente
  // Phasen (analyzing/searching) bewusst nicht — ein Refresh mittendrin
  // restauriert den Stand DAVOR, statt einen halben (bezahlten) Engine-Lauf
  // doppelt zu feuern. Landet die Engine-Antwort, flippt die Phase auf
  // questions/results und der Effekt speichert den frischen Stand mit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: qaRef/prismRunIdRef sind Refs — sie ändern sich im Gleichschritt mit qStep/analyze/engine und werden hier frisch gelesen; `demo` ist eine stabile Prop.
  useEffect(() => {
    if (demo) return;
    if (phase === 'analyzing' || phase === 'searching') return;
    saveFunnelSnapshot({
      phase,
      query,
      website,
      qStep,
      qa: qaRef.current,
      analyze,
      engine,
      prismRunId: prismRunIdRef.current,
      plan,
    });
  }, [phase, query, website, qStep, analyze, engine, plan]);
  // D2 (LEA-430 #3): der Server hält jeden fertigen Lauf 7 Tage (PRISM_RUN_TTL) —
  // Shankys Snapshot trägt nur den GLEICHEN Tab (sessionStorage, 24h). Kommt der
  // User später/im neuen Tab wieder, prüfen wir beim Mount den jüngsten Lauf;
  // NUR status='awaiting_payment' (fertig gesucht, nicht gekauft) kommt als
  // Angebot zurück — gekaufte/laufende/abgelaufene Läufe nie. Explizite
  // „Willkommen zurück"-Leiste, kein Auto-Jump (stiller Sprung = Dark-Pattern).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once (Komfort-Check); demo ist eine stabile Prop.
  useEffect(() => {
    if (demo) return;
    // Gleicher Tab: Snapshot-Restore (oben) ist reicher und hat Vorrang.
    if (loadFunnelSnapshot()) return;
    // Stripe-Cancel-Rückkehr (?resumeRun=) hat ebenfalls Vorrang.
    if (new URLSearchParams(window.location.search).get('resumeRun')) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`${PRISM_RUN_URL}/latest`, { credentials: 'include' });
        if (!res.ok) return;
        const data = (await res.json()) as {
          resume?: boolean;
          target?: string;
          createdAt?: number;
        } & EngineResult;
        if (!alive || !data.resume || !data.companies?.length) return;
        // Nutzer ist schon wieder unterwegs (tippt/sucht) → nicht reinfunken.
        if (phaseRef.current !== 'target') return;
        setResumeOffer(data);
        pushDataLayer('prism_resume_offered', {});
      } catch {
        // Komfort-Endpoint — scheitert still, normaler Funnel-Start bleibt.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  // Angebots-Schalter vom Server. Demo: ?delivery=1 zeigt die Variante mit wöchentlicher
  // Lieferung (nur zur Vorschau, der echte Funnel liest ausschließlich den Server).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once; `demo` ist eine stabile Prop.
  useEffect(() => {
    if (demo) {
      if (new URLSearchParams(window.location.search).get('delivery') === '1')
        setCfg((c) => ({ ...c, deliveryEnabled: true }));
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const r = await fetch(PRISM_CONFIG_URL, { credentials: 'include' });
        if (!r.ok) return;
        const d = (await r.json()) as Partial<PrismConfig>;
        if (!alive) return;
        setCfg({
          deliveryEnabled: d.deliveryEnabled === true,
          weeklyCompanies:
            typeof d.weeklyCompanies === 'number' && d.weeklyCompanies > 0
              ? d.weeklyCompanies
              : DEFAULT_PRISM_CONFIG.weeklyCompanies,
          starterCreditsPerMonth:
            typeof d.starterCreditsPerMonth === 'number' && d.starterCreditsPerMonth > 0
              ? d.starterCreditsPerMonth
              : DEFAULT_PRISM_CONFIG.starterCreditsPerMonth,
        });
      } catch {
        // Ohne Antwort bleibt der heutige Text (Credits pro Monat) — nie mehr versprechen.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  // Each step gets a viewed→completed pair so a drop-off report can compute per-step
  // abandon. Seen-set guard → lang re-renders / phase re-entry don't double-fire.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on phase only.
  useEffect(() => {
    if (stepsSeenRef.current.has(phase)) return;
    stepsSeenRef.current.add(phase);
    pushDataLayer('prism_step_viewed', { step: phase });
  }, [phase]);
  // Alle laufenden Timer-Ids, damit wir bei Unmount sauber clearen können.
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const schedule = (fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  };

  // Clear timers and number animation on unmount.
  useEffect(
    () => () => {
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current = [];
      if (morphRafRef.current) cancelAnimationFrame(morphRafRef.current);
    },
    [],
  );

  // Live-Engine EINMAL feuern, sobald die Analyse startet (phase → 'analyzing').
  // Läuft im Hintergrund weiter, während der Funnel animiert; das Ergebnis füllt
  // Fragen/Firmen/Marktzahl. Bei Server-aus → Fallback auf Mock (kein Bruch).
  // biome-ignore lint/correctness/useExhaustiveDependencies: query/website absichtlich keine Deps — der Fetch feuert genau EINMAL beim Phasenwechsel (analyzeFiredRef) und liest die Werte frisch aus dem Closure.
  useEffect(() => {
    if (phase !== 'analyzing' || analyzeFiredRef.current) return;
    analyzeFiredRef.current = true;
    analyzedQueryRef.current = query.trim(); // F6: dieser Lauf gehört zu DIESEM Zieltext
    // The billable /analyze kickoff (vs. prism_website_submitted = user intent) —
    // the boundary the server first sees, so the pre-/analyze funnel closes here.
    pushDataLayer('prism_analyze_started', { hasWebsite: !!website.trim() });
    // Demo (unauth /dev/prism-demo, Engine=401): Mock-ICP nur, wenn eine
    // Website eingegeben wurde — damit die „dein Unternehmen verstanden"-Karte
    // (Baustelle ⑤) im Review sichtbar ist. Echter Flow bleibt unberührt.
    const demoIcp: AnalyzeResult['icp'] =
      demo && website.trim()
        ? {
            business_model: t('prism.understood.demoModel'),
            opportunities: [
              t('prism.understood.demoOpp1'),
              t('prism.understood.demoOpp2'),
              t('prism.understood.demoOpp3'),
            ],
            risks: [t('prism.understood.demoRisk1'), t('prism.understood.demoRisk2')],
          }
        : undefined;
    // A2: mint a fresh Turnstile token first (no-op '' when disabled), then fetch.
    void (async () => {
      const turnstileToken = turnstileEnabled ? await getTurnstileToken() : '';
      fetch(PRISM_ANALYZE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target: query,
          website,
          lang: LangStore.get(),
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`analyze ${r.status}`))))
        .then((data: AnalyzeResult) => {
          if (data.prismRunId) prismRunIdRef.current = data.prismRunId;
          setAnalyze(data);
        })
        .catch(() => setAnalyze({ error: 'unreachable', icp: demoIcp })); // fail-open → Mock
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Re-present bei Sprachwechsel MITTEN im Funnel (Janik 04.07.): wechselt der
  // User die Sprache, nachdem die Engine schon gelaufen ist, betexten wir den
  // bereits gefundenen Lauf in der neuen Sprache neu — OHNE neue Suche und mit
  // eingefrorenen Scores/strong (nur die Texte ändern sich). Statische UI läuft
  // eh reaktiv über i18n; dieser Effekt holt NUR die Engine-Texte nach.
  const prevLangRef = useRef(lang);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reagiert bewusst NUR auf lang (phase/demo via Guard/Ref gelesen); Setter/Refs sind stabil.
  useEffect(() => {
    const prev = prevLangRef.current;
    prevLangRef.current = lang;
    if (prev === lang || demo) return; // kein Wechsel bzw. Mock-Sandbox
    const runId = prismRunIdRef.current;
    if (!runId) return; // vor /analyze nichts zu re-betexten
    if (phase === 'target' || phase === 'website' || phase === 'analyzing') return;
    if (!analyzeRef.current && !engineRef.current) return; // keine Engine-Daten
    let cancelled = false;
    setRepresenting(true);
    fetch(PRISM_REPRESENT_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prismRunId: runId, lang }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`represent ${r.status}`))))
      .then((data: RepresentResult) => {
        if (cancelled) return;
        const nd = (d?: string) => (d || '').toLowerCase().replace(/^www\./, '');
        // Fragen + Verstanden-Karte (icp) in der Analyse-Phase.
        setAnalyze((a) =>
          a ? { ...a, questions: data.questions ?? a.questions, icp: data.icp ?? a.icp } : a,
        );
        // Firmen-Begründungen (Score behalten), Vorschläge, Markt-Text, icp.
        setEngine((e) => {
          if (!e) return e;
          const reasonByDom = new Map(
            (data.companies ?? []).map((c) => [nd(c.domain), c.fit_reason]),
          );
          const companies = (e.companies ?? []).map((co) => {
            const nr = reasonByDom.get(nd(co.domain));
            return nr ? { ...co, fit_reason: nr } : co;
          });
          return {
            ...e,
            companies,
            icp: data.icp ?? e.icp,
            market: data.market ?? e.market,
            questions: data.questions?.map((q) => q.q) ?? e.questions,
            quality: e.quality
              ? { ...e.quality, ...(data.suggestions ? { suggestions: data.suggestions } : {}) }
              : e.quality,
          };
        });
      })
      .catch(() => {
        // fail-open: alte Texte behalten, Funnel nie brechen
      })
      .finally(() => {
        if (!cancelled) setRepresenting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // IntersectionObserver für die fixe Kauf-Leiste — nur in der Paywall-Phase
  // aktiv. Sichtbarkeits-Set statt Einzel-Flag, weil zwei CTAs beobachtet werden.
  useEffect(() => {
    if (phase !== 'paywall') {
      setCtaOffscreen(false);
      return;
    }
    const targets = [vaultCtaRef.current, planCtaRef.current].filter(
      (el): el is HTMLButtonElement => el !== null,
    );
    if (!targets.length || typeof IntersectionObserver === 'undefined') return;
    const visible = new Set<Element>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.add(e.target);
          else visible.delete(e.target);
        }
        setCtaOffscreen(visible.size === 0);
      },
      { threshold: 0.05 },
    );
    for (const t of targets) io.observe(t);
    return () => io.disconnect();
  }, [phase]);

  // ─── Aktionen (Port der Klassen-Methoden) ────────────────────────────────

  // Finale "Suche" mit hochzählendem Treffer-Counter → Ergebnis (Aha).
  function runSearch() {
    // D2: jede FRISCHE Suche beendet den Resume-Zustand — sonst würde die
    // „Auswahl gespeichert"-Leiste auf einem brandneuen Lauf stehen bleiben
    // (Review-Finding 12.07.).
    setRunResumed(false);
    const steps = searchStepsFor();
    // Platzhalter-Ziel des Zählers, solange die Engine läuft. Früher `metrics.universe`
    // (Hash-Wert 2.400–8.600, ohne Datenbasis); reale Läufe entdecken ~70–140 Firmen, der
    // Zähler stürzte am Ende von Tausenden auf zweistellig. Jetzt dieselbe Größenordnung
    // wie das echte Ergebnis; sobald die Engine antwortet, gilt `discovery.rawFound`.
    const target = 90 + ((metrics.universe / 10) % 60);
    setPhase('searching');
    phaseRef.current = 'searching';
    setCount(0);
    // #3: frischer Lauf → Morph-Zustand zurücksetzen (Headline liest wieder kept).
    if (morphRafRef.current) cancelAnimationFrame(morphRafRef.current);
    setMorphCount(null);
    setDoneSteps([]);
    setLoadMsg(steps[0].t);
    // Jetzt die eigentliche Suche (Discovery + Fit + Audit) feuern — sie läuft
    // WÄHREND dieser Animation. Die Fertig-Logik unten wartet auf das Ergebnis.
    if (!searchFiredRef.current) {
      searchFiredRef.current = true;
      // A2: fresh single-use Turnstile token (separate from /analyze's), then fetch.
      // Runs in parallel with the search animation above → no perceived latency.
      void (async () => {
        const turnstileToken = turnstileEnabled ? await getTurnstileToken() : '';
        fetch(PRISM_SEARCH_URL, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            target: query,
            icp: analyzeRef.current?.icp ?? null,
            withAudit: true,
            lang: LangStore.get(),
            prismRunId: prismRunIdRef.current,
            // Antworten der Fragen-Phase → verfeinern Query-Planung + Fit-Scoring.
            qa: qaRef.current.length ? qaRef.current : undefined,
            ...(turnstileToken ? { turnstileToken } : {}),
          }),
        })
          .then(async (r) => {
            if (r.ok) return r.json() as Promise<EngineResult>;
            if (r.status === 429) {
              // F9: Kontingent-Grenze — Reset-Zeit aus X-RateLimit-Reset
              // (Epoch-Sekunden Fensterende); die exakte Cap-Zahl leaken wir
              // nicht in die Copy (LEA-350-Prinzip). Scope nur ins Analytics.
              const resetSec = Number(r.headers.get('X-RateLimit-Reset'));
              setRateLimitResetMs(
                Number.isFinite(resetSec) && resetSec > 0 ? resetSec * 1000 : null,
              );
              let scope = '';
              try {
                scope = ((await r.json()) as { scope?: string }).scope ?? '';
              } catch {
                // Body optional — Header trägt die relevante Information.
              }
              pushDataLayer('prism_search_rate_limited', { scope });
              throw new Error(RATE_LIMIT_SIGNAL);
            }
            throw new Error(`search ${r.status}`);
          })
          .then((data: EngineResult) => {
            if (data.prismRunId) prismRunIdRef.current = data.prismRunId;
            setEngine(data);
          })
          .catch(async (e: unknown) => {
            // F9: 429 ist final für dieses Fenster — kein Run-Poll (es lief keine
            // Suche an), direkt in den ehrlichen Kontingent-Zustand.
            if (e instanceof Error && e.message === RATE_LIMIT_SIGNAL) {
              setEngineErr(RATE_LIMIT_SIGNAL);
              return;
            }
            // A2 (Janik 07.07.): the fetch may have failed because the ~60s gateway
            // 504'd a slow /search while the backend kept running + persisted the
            // result. Poll the saved run before giving up, so a slow-but-successful
            // search still delivers the paywall instead of a false "too broad".
            const recovered = await pollRunResult(prismRunIdRef.current);
            if (recovered) {
              if (recovered.prismRunId) prismRunIdRef.current = recovered.prismRunId;
              setEngine(recovered);
            } else {
              setEngineErr(e instanceof Error ? e.message : String(e));
            }
          });
      })();
    }
    // ── Pacing (Janik 03.07.): Animation an die ECHTE Engine-Dauer gekoppelt.
    // Vorher: 9 Steps × 720ms = 6,5s Choreografie, Engine 20-45s → vorne raste
    // alles durch, hinten stand 15-38s ein toter Spinner. Jetzt: die Timeline
    // ist auf die erwartete Engine-Zeit kalibriert (Discovery/Fit/Audit aus
    // meta.timings ≈ 20-24s p50), hinten elastisch — der LETZTE Schritt
    // schließt erst mit dem echten Ergebnis, bis dahin sichtbare Mikro-
    // Aktivität (rotierende ehrliche Warte-Zeilen + weitertickender Zähler).
    // Antwortet die Engine früher, schließt alles in einem sanften Finish.
    const t0 = Date.now();
    // Janik 04.07.: mehr Runway/Puffer — bei 26s erreichte der Zähler zu früh das
    // Ziel und stand dann bei langsamen/Rescue-Läufen (40s+) tot still. 32s legt
    // den 90%-Punkt später; der steigende Deckel unten hält den Rest in Bewegung.
    const EXPECTED_MS = 32000;
    const MIN_SHOW_MS = 9000; // auch bei Blitz-Antwort (Mock/Fehler) nie unter ~9s Story
    // Nicht-uniforme Verteilung: hintere Schritte dauern länger (fühlt sich
    // nach echter Pipeline an, nicht nach Abspielliste). Schritte 1..n-1 auf
    // ~86% der erwarteten Zeit; der letzte bleibt offen bis zum Ergebnis.
    // #2 (Welle 2): Gewichtung 0.35→0.55 — die hinteren Step-Wechsel liegen
    // später/weiter auseinander, die letzten 2 Schritte bekommen sichtbar mehr
    // Luft statt vorne durchzurasen.
    const weights = steps.map((_, i) => 1 + i * 0.55);
    const wSum = weights.reduce((a, b) => a + b, 0);
    const stepAt: number[] = [];
    let accMs = 0;
    for (let k = 0; k < steps.length - 1; k++) {
      accMs += (weights[k] / wSum) * EXPECTED_MS * 0.86;
      stepAt.push(accMs);
    }
    for (let k = 0; k < steps.length - 1; k++) {
      schedule(() => {
        if (phaseRef.current !== 'searching') return;
        setDoneSteps(steps.slice(0, k + 1));
        setLoadMsg(steps[k + 1].t);
      }, stepAt[k]);
    }
    // Zähler organisch: unregelmäßige Schübe mit Pausen Richtung ease-out-Ziel
    // (92%-Deckel solange die Engine läuft), Mikro-Ticken im Wartefenster —
    // niemals ein stehendes Bild. Voll läuft er erst mit echtem Ergebnis.
    let shown = 0;
    const tick = () => {
      if (phaseRef.current !== 'searching') return;
      const el = Date.now() - t0;
      const ready = !!(engineRef.current || engineErrRef.current);
      // #3 (Welle 2): sobald die Engine da ist, das Zähler-Ziel von der
      // synthetischen metrics.universe auf den ECHTEN discovery.rawFound
      // umklemmen (Fallback: aktuelles Ziel) — der Ruhewert VOR dem Morph ist
      // damit echt, nicht erfunden.
      // `kept` (nach Junk-Filter) statt `rawFound` (roh, inkl. Dubletten/Portale): der Zähler
      // soll auf die Zahl zulaufen, die das Ergebnis danach wirklich nennt.
      const liveTarget =
        engineRef.current?.discovery?.kept ?? engineRef.current?.discovery?.rawFound ?? target;
      const pTime = Math.min(1, el / EXPECTED_MS);
      // Deckel-Kern-Fix (Janik 04.07.): KEIN festes Plateau, das „steht still"
      // wirkt. Bis EXPECTED_MS steigt der Deckel per ease-out auf ~0.90; danach
      // (Overrun, Engine noch am Arbeiten) kriecht er sichtbar WEITER (~+0.4%/s
      // bis max 0.985), statt einzufrieren. Mit echtem Ergebnis sofort 1.0.
      const overrunS = Math.max(0, el - EXPECTED_MS) / 1000;
      const cap = ready ? 1 : Math.min(0.985, 0.9 + overrunS * 0.004);
      const ceiling = Math.round((1 - (1 - pTime) ** 2) * cap * liveTarget);
      // IMMER in Bewegung: nähere dich dem Ziel um einen Bruchteil der Rest-
      // distanz (mind. 1) — sanftes, stetiges Auslaufen statt hartem Stopp +
      // Zufalls-Zappeln. Fühlt sich nach echter Verarbeitung an, nie eingefroren.
      if (shown < ceiling) {
        shown = Math.min(ceiling, shown + Math.max(1, Math.round((ceiling - shown) * 0.2)));
        setCount(shown);
      }
      if (shown < liveTarget || !ready) schedule(tick, 140 + Math.random() * 160);
    };
    schedule(tick, 250);
    // Nicht blind zu 'results' springen — auf das echte Engine-Ergebnis warten
    // (oder Fehler/Timeout 75s: der Rescue-Retry ~+20s darf nie im Fallback enden).
    // #2 (Welle 2): 6 ehrliche Prozess-Zeilen (statt 4) — die Schlussphase
    // realer Läufe (>45s) rotiert endlos durch WAHRE Prozessbeschreibungen
    // (Duplikat-Abgleich, Erreichbarkeit, Adress-Verifikation …), nie ein
    // stehender Satz.
    const WAIT_MSGS = [
      t('prism.searching.wait1'),
      t('prism.searching.wait2'),
      t('prism.searching.wait3'),
      t('prism.searching.wait4'),
      t('prism.searching.wait5'),
      t('prism.searching.wait6'),
    ];
    const lastStepDone = stepAt[stepAt.length - 1] ?? 0;
    let closing = false;
    const finish = () => {
      if (phaseRef.current !== 'searching') return;
      const ready = engineRef.current || engineErrRef.current;
      if ((ready && !closing) || Date.now() - t0 > 75000) {
        // Sanftes Finish statt Sprung: Rest-Schritte schließen sichtbar,
        // der Zähler läuft voll (tick, cap=1), erst dann der Phasen-Wechsel.
        closing = true;
        setDoneSteps(steps);
        setLoadMsg('');
        schedule(() => {
          if (phaseRef.current !== 'searching') return;
          setPhase('results');
          phaseRef.current = 'results';
          // #3 (Welle 2): bewusster Morph beim Reveal statt hartem Sprung.
          // Quelle = zuletzt gezeigter Zähler (≈ rawFound), Ziel = echte kept.
          // 700ms easeOutCubic. Ohne echte kept (Demo/Mock) bleibt die Quelle.
          const morphFrom = shown;
          const morphTo = engineRef.current?.discovery?.kept ?? shown;
          const reduceMorph =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          if (reduceMorph || morphFrom === morphTo) {
            setMorphCount(morphTo); // reduced-motion: sofort final, kein Zwischenwert
          } else {
            setMorphCount(morphFrom);
            const mt0 = performance.now();
            const mDur = 700;
            const mStep = (now: number) => {
              const p = Math.min(1, (now - mt0) / mDur);
              const eased = 1 - (1 - p) ** 3;
              setMorphCount(Math.round(morphFrom + (morphTo - morphFrom) * eased));
              if (p < 1) morphRafRef.current = requestAnimationFrame(mStep);
            };
            morphRafRef.current = requestAnimationFrame(mStep);
          }
          // G1 (LEA-379): results reached — the funnel's key conversion signal.
          let evSource = 'timeout';
          if (engineRef.current) evSource = 'engine';
          else if (engineErrRef.current) evSource = 'error';
          pushDataLayer('prism_search_completed', {
            realCount: engineRef.current?.discovery?.kept ?? null,
            engineWeak: !!engineRef.current?.quality && !engineRef.current.quality.strong,
            source: evSource,
            ms: Date.now() - t0,
          });
        }, 900);
        return;
      }
      if (!closing) {
        const waited = Date.now() - t0 - lastStepDone;
        if (waited > 0) {
          // #2 (Welle 2): Modulo-Zyklus statt Index-Cap — die Schlussphase
          // rotiert endlos alle ~3,8s durch die 6 Prozess-Zeilen, friert nie ein.
          const idx = Math.floor(waited / 3800);
          setLoadMsg(WAIT_MSGS[idx % WAIT_MSGS.length]);
        }
        schedule(finish, 600);
      }
    };
    schedule(finish, MIN_SHOW_MS);
  }

  function runAnalyze() {
    const steps = analyzeSteps();
    const per = ANALYZE_PER;
    const t0 = Date.now();
    setPhase('analyzing');
    phaseRef.current = 'analyzing';
    setDoneSteps([]);
    setLoadMsg(steps[0].t);
    for (let k = 0; k < steps.length; k++) {
      schedule(
        () => {
          if (phaseRef.current !== 'analyzing') return;
          setDoneSteps(steps.slice(0, k + 1));
          setLoadMsg(k + 1 < steps.length ? steps[k + 1].t : '');
        },
        (k + 1) * per,
      );
    }
    // Erst zur Fragen-Phase, wenn die ECHTEN Fragen (analyzePhase) da sind —
    // sonst sähe der User generische Platzhalter. Timeout → fail-open (Mock).
    const finishAnalyze = () => {
      if (phaseRef.current !== 'analyzing') return;
      if (analyzeRef.current || Date.now() - t0 > 25000) {
        setPhase('questions');
        phaseRef.current = 'questions';
        setQStep(0);
        qaRef.current = []; // frische Runde → keine Antworten aus einem Vorlauf
      } else {
        setLoadMsg(t('prism.analyzing.wait'));
        schedule(finishAnalyze, 500);
      }
    };
    schedule(finishAnalyze, steps.length * per + 600);
  }

  // Aktuelle Frage (Engine mit Antwort-Chips ODER Mock/String-Fallback) —
  // eine Quelle für Render, answerQuestion UND Keyboard-Handler.
  const getQuestionAt = (i: number): FunnelQuestion => {
    const src: Array<string | FunnelQuestion> = analyzeRef.current?.questions?.length
      ? analyzeRef.current.questions
      : questions();
    const raw = src[i] ?? src[0];
    if (typeof raw === 'string') return { q: raw, options: [] };
    return raw ?? { q: '', options: [] };
  };
  const getQuestionCount = (): number =>
    analyzeRef.current?.questions?.length || questions().length;

  function commitAnswer(answer: string) {
    // Antwort festhalten — steuert die Suche real (qa → planQueries/fit), nicht
    // nur Analytics/Blackbox (Janik 03.07.: das Genauigkeits-Versprechen muss stimmen).
    // Index-Zuweisung statt push: Doppelklick/Key-Repeat auf dieselbe Frage
    // überschreibt statt doppelt zu loggen.
    const next = [...qaRef.current];
    next[qStep] = { question: getQuestionAt(qStep).q, answer };
    qaRef.current = next;
    pushDataLayer('prism_question_answered', { step: qStep + 1 });
    const n = qStep + 1;
    if (n >= getQuestionCount()) runSearch();
    else {
      setQDir(1); // nächste Frage läuft von rechts ein
      setQStep(n);
    }
  }

  // A4-6: Commit-Beat — die gewählte Karte wird ~160ms sichtbar „registriert"
  // (is-picked), erst danach advancet der Flow. Fühlt sich nach „erkannt" statt
  // „weg" an. pickingRef verhindert Doppel-Trigger (Klick + Key gleichzeitig).
  function answerQuestion(answer: string) {
    if (pickingRef.current) return;
    pickingRef.current = true;
    setPicked(answer);
    schedule(() => {
      pickingRef.current = false;
      setPicked(null);
      commitAnswer(answer);
    }, 160);
  }

  // Zurück in der Fragen-Phase (Janik: „was, wenn sich jemand verklickt?").
  // Der Funnel war strikt vorwärts — ein Fehlklick auf einen Chip war nur per
  // Komplett-Neustart korrigierbar (verbraucht einen Run gegen PRISM_MAX_RUNS).
  // Frage 2/3 → eine Frage zurück (vorige Antwort bleibt in qaRef als Vorgabe,
  // Re-Antwort überschreibt sie). Frage 1 → zurück zum Ziel-Feld (query bleibt
  // als controlled input erhalten, Zielgruppe editierbar ohne Datenverlust).
  function goBackQuestion() {
    if (qStep > 0) {
      setQDir(-1); // vorige Frage läuft von links ein (A4-7)
      setQStep(qStep - 1);
    } else {
      setPhase('target');
    }
  }

  // Tastatur-Shortcuts der Fragen-Phase (Om QA 2026-07-03 + Chips Janik 03.07.):
  // mit Antwort-Chips wählen die Ziffern 1-3 die Option; im Ja/Nein-Fallback
  // (Frage ohne Optionen) bleiben J/N. Re-attached per qStep, damit immer die
  // aktuelle Frage beantwortet wird (kein stale closure).
  // biome-ignore lint/correctness/useExhaustiveDependencies: answerQuestion/getQuestionAt sind pro Render frisch; [phase, qStep] re-bindet den Listener mit aktuellem State.
  useEffect(() => {
    if (phase !== 'questions') return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      const k = e.key.toLowerCase();
      if (k === 'backspace') {
        e.preventDefault();
        goBackQuestion();
        return;
      }
      const opts = getQuestionAt(qStep).options;
      if (opts.length >= 2) {
        const idx = Number.parseInt(k, 10) - 1;
        if (idx >= 0 && idx < opts.length) {
          e.preventDefault();
          answerQuestion(opts[idx]);
        }
        return;
      }
      if (k === 'j') {
        e.preventDefault();
        answerQuestion(t('prism.questions.yes'));
      } else if (k === 'n') {
        e.preventDefault();
        answerQuestion(t('prism.questions.no'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, qStep]);

  // Server-Kopie der Schritte vor der Zahlung. Nicht in der Demo: dort gibt es keine Sitzung.
  function track(name: PrismServerEvent, from?: PrismEventFrom, kind?: string) {
    if (demo) return;
    sendPrismEvent(name, prismRunIdRef.current, from, undefined, kind);
  }
  function enterPaywall() {
    setPhase('paywall');
    phaseRef.current = 'paywall';
    pushDataLayer('prism_paywall_viewed', {});
    track('prism_offer_details_viewed', 'results');
  }
  // D2: „Willkommen zurück" — expliziter Klick re-hydriert den gespeicherten
  // Lauf (identischer Pfad wie der Stripe-Cancel-Resume). Kein Auto-Jump.
  function acceptResume() {
    const data = resumeOffer;
    if (!data) return;
    setResumeOffer(null);
    if (data.target) setQuery(data.target);
    if (data.prismRunId) prismRunIdRef.current = data.prismRunId;
    searchFiredRef.current = true; // kein erneuter Suchlauf
    setRunResumed(true);
    setEngine(data);
    setPhase('results');
    phaseRef.current = 'results';
    pushDataLayer('prism_resume_accepted', {});
  }
  function dismissResume() {
    setResumeOffer(null);
    pushDataLayer('prism_resume_dismissed', {});
  }

  function openCheckout(p: Plan, from: PrismEventFrom = 'offer') {
    coOpenedAtRef.current = Date.now();
    setPlan(p);
    setPaying(false);
    setPaid(false);
    setEmbedFallback(false); // frischer Embed-Versuch bei jedem Öffnen
    // Ebenso das Zahlungsformular: ein einmaliger Fehlschlag der Probe (Netz, Neustart der
    // API) ließ bisher für den Rest der Sitzung nur noch den Weiterleitungs-Knopf stehen.
    setElementFallback(false);
    pushDataLayer('prism_checkout_opened', { plan: p.name, price: p.price, from });
    track('prism_checkout_opened', from);
  }

  function submitPay() {
    // s. coJustOpened: kein Bezahlen durch den zweiten Klick eines Doppelklicks.
    if (Date.now() - coOpenedAtRef.current < 500) return;
    setPaying(true);
    if (demo) {
      // /dev/prism-demo sandbox: no auth/Stripe → keep the fake success so the
      // standalone demo still completes the funnel.
      schedule(() => {
        setPaying(false);
        setPaid(true);
      }, 1100);
      return;
    }
    // B7 (LEA-379): real 49 € tripwire. Hand off to Stripe hosted Checkout via
    // the global bridge (redirect launch; inline Payment Element is Stufe 4).
    // On success Stripe returns to /billing/success and the webhook grants the
    // 50 credits + schedules the day-4 Starter + seeds the account. prismRunId
    // links the funnel run so the seed pulls from it (S4).
    pushDataLayer('prism_payment_submitted', {});
    track('prism_payment_submitted');
    const start = (
      window as unknown as { __startPrismCheckout?: (prismRunId?: string) => Promise<void> }
    ).__startPrismCheckout;
    if (start) {
      void start(prismRunIdRef.current ?? undefined);
      // stays in `paying` until the browser navigates away to Stripe
    } else {
      setPaying(false);
    }
  }

  // ─── Abgeleitete Render-Werte (Port von renderVals) ──────────────────────

  const mockSet = useMemo(() => leadSetFor(query), [query]);
  // Geseedete Kennzahlen für Scan-Animation + Ergebnis-Statistiken: Query +
  // Lauf-Salt → bei JEDEM Durchlauf leicht andere, in sich konsistente Werte
  // (Janik: „immer ein bisschen unterschiedlich, das wirkt realistischer").
  // Als Schätzungen geframt; harte Zahlen kommen weiter aus der Engine.
  const [runSalt, setRunSalt] = useState(() => Math.random().toString(36).slice(2));
  const metrics = useMemo(() => seededMetrics(`${query}::${runSalt}`), [query, runSalt]);
  // A5-9: Gesamt-Step-Zahl der Suche → determinater Fortschritts-Faden
  // (erledigte / gesamt). Gleiche Quelle wie die StepList in runSearch.
  const searchStepTotal = useMemo(() => searchStepsFor().length, []);
  // Echte Engine-Firmen → Lead-Form (mit echter Domain → echtes Favicon, echter
  // Fit-Score → Badge, echte Fit-Begründung). Fallback = Mock-Set.
  const engLeads: Lead[] | null = useMemo(() => {
    const cs = engine?.companies;
    if (!cs || !cs.length) return null;
    return cs.map((c) => ({
      in: initials(c.name),
      co: c.name.replace(/\s*[|·–-]\s*.*$/, '').trim() || c.name,
      meta: c.address || (engine?.icp?.business_model ? '' : ''),
      name: '',
      role: '',
      // Plausible AP-Schätzung je Firma (2–6, stabil je Domain) — „0 Ansprech-
      // partner" wirkt wie ein Fehlschlag; echte Kontakte kommen nach Kauf.
      ap: 0, // keine Hash-Zahl mehr: Ansprechpartner werden erst nach dem Kauf recherchiert
      domain: c.domain,
      score: typeof c.fit_score === 'number' ? c.fit_score : undefined,
      fitReason: c.fit_reason,
      evidence: c.evidence,
    }));
  }, [engine]);
  const leadSet = engLeads ?? mockSet;
  const resultLeads = leadSet.slice(0, Math.min(4, leadSet.length));
  // Die stärkste Firma (höchster Fit) — dieselbe Regel wie der Teaser auf dem Server.
  const freeLead: Lead = leadSet.reduce(
    (best, l) => ((l.score ?? -1) > (best.score ?? -1) ? l : best),
    leadSet[0],
  );
  // #6 (Welle 2): EINE Verdichtungs-Erzählung aus echten Lauf-Daten. Dieselben
  // drei Zahlen tragen ALLE Screens (Ergebnis-Headline, Stat-Tiles, Vault-Kopf)
  // mit identischer Vokabel — kein synthetischer Wert (metrics.ap/revenueM …)
  // überlebt im Ergebnis. Stufe 1 „live geprüft" = discovery.kept, Stufe 2
  // „passen zu deiner Zielgruppe" = quality.onTargetCount, Stufe 3 „deine 50
  // stärksten" = min(50, onTarget) (Produkt-Versprechen, an echte Zahl gedeckelt).
  const kept = engine?.discovery?.kept ?? null;
  const searchQueries: string[] = useMemo(() => {
    const qs = engine?.discovery?.queries ?? (demo ? DEMO_QUERIES : []);
    return [...new Set(qs.map((q) => q.trim()).filter(Boolean))];
  }, [engine, demo]);
  const onTarget = engine?.quality?.onTargetCount ?? null;
  // Zusage vor dem Kauf (08.10.2026): Der Kauf liefert die passenden Firmen DIESES Laufs,
  // nicht pauschal 50. Kein Käufer hat bisher 50 bekommen (5 bis 35). Liegt der Lauf unter
  // dem Startguthaben, sagt das Bezahlfenster die echte Zahl und was mit dem Rest geschieht.
  const deliverable = engine?.quality?.deliverableCount ?? null;
  const showDeliverNote = deliverable != null && deliverable >= 2 && deliverable < 50;
  // Dieselbe Zahl trägt alle Angebots-Texte (15.10.2026, Janik: „erst Bezahlfenster und Texte
  // reparieren“): Ergebnisliste, Angebotsseite, Bezahlfenster und Erfolg sagen {n} statt der
  // festen 50. Gleiche Grenzen wie showDeliverNote: unbekannt (Lauf ohne Pool, ältere API),
  // 0 oder 1 → 50 wie bisher. Bei 1 entstünden Sätze wie „Alle 1 freischalten“ oder „Die
  // übrigen 0“ (Review 15.10.); 0 und 1 sind schwache Läufe, über deren Kaufangebot gesondert
  // entschieden wird.
  const leadN = showDeliverNote ? deliverable : 50;
  const leadVars = { n: leadN, rest: Math.max(leadN - 1, 0) };
  const keptFmt = kept != null ? kept.toLocaleString('de-DE') : '—';
  const onTargetFmt = onTarget != null ? onTarget.toLocaleString('de-DE') : '—';
  // Ø Fit-Score der Treffer (echt) — ersetzt das synthetische „Umsatzpotenzial".
  const avgFit = engine?.quality?.avgFit ?? null;
  // Markt-Hochrechnung (echt, in der Engine bereits als Schätzung markiert).
  const marketRange = engine?.market?.projected_range_dach || '';
  // #13: kompakte Zahlen-Spanne für die Markt-Kachel — die Engine-Strings sind
  // frei formuliert ("ca. 200–350 (Schätzung)", "150-350 établissements …");
  // fürs Tile ziehen wir nur die Spanne, sonst sprengt Text die Kachel.
  const marketTileValue = useMemo(() => {
    const m = marketRange.match(/[~≈]?\s*\d[\d.,]*\s*[–—-]\s*\d[\d.,]*/);
    return m ? `≈${m[0].replace(/^[~≈]\s*/, '')}` : null;
  }, [marketRange]);
  // #5 (Welle 2): die Wand echter Treffer aus dem vollen gescorten Pool
  // (Superset der kuratierten Anzeige). Nur fit ≥ 40, absteigend sortiert. Fehlt
  // poolCompanies (Alt-Lauf/Demo) → leer, das Top-5-Verhalten bleibt unberührt.
  const poolLeads: Lead[] = useMemo(() => {
    // Demo-Sandbox ohne Engine: Wand aus DEMO_POOL_COMPANIES speisen, damit der
    // /dev/prism-demo Preview die volle Vault-Wand zeigt (nie im Kunden-Pfad).
    const pool = engine?.poolCompanies?.length
      ? engine.poolCompanies
      : demo
        ? DEMO_POOL_COMPANIES
        : undefined;
    if (!pool?.length) return [];
    return pool
      .filter((c) => (c.fit_score ?? 0) >= 40)
      .sort((a, b) => (b.fit_score ?? 0) - (a.fit_score ?? 0))
      .map((c) => ({
        in: initials(c.name),
        co: c.name.replace(/\s*[|·–-]\s*.*$/, '').trim() || c.name,
        meta: [c.address?.split(',').slice(-2).join(',').trim(), c.desc]
          .filter(Boolean)
          .join(' · '),
        name: '',
        role: '',
        ap: 0, // keine Hash-Zahl mehr: Ansprechpartner werden erst nach dem Kauf recherchiert
        domain: c.domain,
        score: typeof c.fit_score === 'number' ? c.fit_score : undefined,
        fitReason: c.fit_reason,
      }));
  }, [engine, demo]);
  // Vault-Top-5: die reichen Zeilen bleiben die kuratierte Anzeige-Menge (wie
  // Ergebnis-Screen) — die Wand darunter kommt aus dem vollen Pool (#5).
  const vaultLeads = leadSet.slice(0, Math.min(5, leadSet.length));
  // Wand-Zeilen: Pool ohne die oben schon gezeigten Top-5 (Domain-Dedup, damit
  // keine Firma doppelt auftaucht), Perf-Cap 55 zusätzliche Rows.
  const shownDomains = new Set(vaultLeads.map((l) => l.domain || l.co));
  const wallLeads = poolLeads.filter((l) => !shownDomains.has(l.domain || l.co)).slice(0, 55);
  // #6: Ergebnis-Headline-Zahl morpht beim Reveal von rawFound → kept. Vor dem
  // Morph (morphCount === null) liest sie den echten kept-Wert; Demo/Mock ohne
  // Engine fällt auf den animierten Zähler zurück.
  const headlineNum = morphCount != null ? morphCount : (kept ?? count);
  const headlineFmt = fmtInt(headlineNum);
  // Weitere geprüfte Firmen über die 4 im Teaser hinaus — echt (kept), Demo-
  // Fallback auf den Scan-Scope. „+ X weitere" bleibt so eine reale Restmenge.
  const moreCount =
    kept != null
      ? Math.max(0, kept - resultLeads.length)
      : Math.max(0, metrics.universe - resultLeads.length);
  // Engine hat geantwortet, aber die Treffer sind zu schwach/unpassend →
  // ehrlicher "präzisieren"-Screen statt Paywall. (Bei Server-aus: engine=null
  // → normaler Mock-Flow bleibt, damit die Standalone-Demo weiter läuft.)
  const engineWeak = !!engine && !!engine.quality && !engine.quality.strong;
  const engineReason = engine?.quality?.reason || '';
  // F2 (LEA-379): im ECHTEN (nicht-demo) Modus NIE stumm auf Mock-Leads
  // zurückfallen. Harte Suche-Fehler (502/Netzwerk) ODER Engine ohne verwertbare
  // Firmen (und kein sauberer „präzisieren"-Fall = engineWeak) → Retry-Screen
  // statt Mock-Paywall. Wir recyceln dafür den refine-Screen (restartFunnel),
  // damit KEIN Mock-Lead gerendert wird (dediziertes Error+Support-UI: Follow-up).
  const hasRealLeads = !!engine?.companies && engine.companies.length > 0;
  // F9: Kontingent-Fall VOR dem generischen Fehler-/Refine-Pfad — eigener Screen.
  const rateLimited = engineErr === RATE_LIMIT_SIGNAL;
  const searchFailed = !demo && !engineWeak && (engineErr !== null || !hasRealLeads);
  // Nenner für „Freischalten“: melden, dass eine starke Liste gezeigt wurde. Einmal je Laden
  // der Seite; nach Neuladen oder Rückkehr von Stripe kommt das Ereignis für denselben Lauf
  // erneut, die Auswertung zählt deshalb je Nutzer und Lauf.
  const resultsSeenRef = useRef<string | null>(null);
  useEffect(() => {
    if (demo || phase !== 'results' || engineWeak || searchFailed || rateLimited) return;
    const key = prismRunIdRef.current ?? 'no-run';
    if (resultsSeenRef.current === key) return;
    resultsSeenRef.current = key;
    sendPrismEvent('prism_results_viewed', prismRunIdRef.current);
  }, [demo, phase, engineWeak, searchFailed, rateLimited]);

  // ── Echter Nutzen vor den 49 € (16.10.2026) ───────────────────────────────
  // Sobald eine starke Ergebnisliste steht: Vorschau anstoßen (Gratis-Leads der stärksten Firmen
  // + Hinweise für die übrigen; der Server macht das höchstens einmal je Lauf und nur ab 2
  // lieferbaren Firmen) und nachfragen, bis sie fertig ist. Ein Phasenwechsel (Angebotsseite)
  // bricht das Nachfragen nicht ab.
  const showTeaserFlow =
    (phase === 'results' || phase === 'paywall') && !engineWeak && !searchFailed && !rateLimited;
  // biome-ignore lint/correctness/useExhaustiveDependencies: läuft je Lauf einmal (teaserRunRef); leadSet nur für die Demo-Vorschau gelesen.
  useEffect(() => {
    if (!showTeaserFlow) return;
    const runId = demo ? `demo:${leadSet[0]?.co ?? ''}` : prismRunIdRef.current;
    if (!runId || teaserRunRef.current === runId) return;
    teaserRunRef.current = runId;
    teaserEventRef.current = null;
    teaserOpenedRef.current = false;
    setPreview(null);
    setTeaserOpen(false);
    if (demo) {
      // /dev/prism-demo: Vorschau aus den Mock-Leads (fiktive Firmen). ?free=0 zeigt den Fall
      // „Kontingent verbraucht“ (maskierter Entscheider), ?teaser=none die ehrlichen
      // Leer-Zustände. Nie im Kunden-Pfad.
      const q = new URLSearchParams(window.location.search);
      const freeN = q.get('free') === '0' ? 0 : 3;
      const none = q.get('teaser') === 'none';
      // ?mails=1: fiktive Adressen für die fiktiven Mock-Firmen, um den Fall „3 von 3“ zu zeigen.
      const fakeMails = q.get('mails') === '1';
      const mailOf = (x: Lead) =>
        x.email ??
        (fakeMails && x.name
          ? `${x.name
              .replace(/^Dr\.\s+/, '')
              .split(' ')[0]?.[0]
              ?.toLowerCase()}.${(x.name.split(' ').pop() ?? '').toLowerCase()}@${x.co
              .toLowerCase()
              .replace(/[^a-z]+/g, '-')
              .replace(/^-|-$/g, '')
              .slice(0, 18)}.de`
          : null);
      const companies: PreviewCompanyView[] = leadSet.slice(0, 8).map((l, i) => {
        const domain = l.domain || l.co;
        const mail = mailOf(l);
        const pattern = mail ? `{f}.{last}@${mail.split('@')[1]}` : null;
        if (none)
          return {
            domain,
            companyName: l.co,
            kind: i === 0 && !freeN ? 'teaser' : 'hint',
            status: 'none',
          };
        // Wie der Server: Gratis-Leads nur mit E-Mail, höchstens freeN; ohne Kontingent die
        // stärkste Firma maskiert.
        const freeIdx = leadSet
          .slice(0, 8)
          .filter((x) => !!mailOf(x))
          .slice(0, freeN);
        if ((freeN > 0 && freeIdx.includes(l)) || (freeN === 0 && i === 0)) {
          return {
            domain,
            companyName: l.co,
            kind: freeN > 0 ? 'free' : 'teaser',
            status: 'found',
            decisionMakers: 2,
            roles: ['Geschäftsführer'],
            emailPattern: pattern,
            sources: ['impressum'],
            person: {
              name: l.name || '—',
              role: l.role || 'Geschäftsführer',
              source: 'impressum',
              sourceUrl: null,
              email: freeN > 0 ? mail : null,
              emailMasked: mail ? `${mail[0]}***@${mail.split('@')[1]}` : null,
              emailStatus: mail ? 'verified' : 'none',
              phone: freeN > 0 ? (l.phone ?? null) : null,
            },
          };
        }
        return {
          domain,
          companyName: l.co,
          kind: 'hint',
          status: 'found',
          decisionMakers: 1 + (i % 2),
          roles: i % 2 ? ['Geschäftsführer', 'Prokurist'] : ['Inhaber'],
          emailPattern: pattern,
          sources: i % 2 ? ['impressum', 'handelsregister'] : ['impressum'],
        };
      });
      setPreview({ status: 'running', freeTarget: freeN, companies: [] });
      schedule(
        () =>
          setPreview({ status: 'running', freeTarget: freeN, companies: companies.slice(0, 1) }),
        1800,
      );
      schedule(
        () =>
          setPreview({
            status: 'done',
            freeTarget: freeN,
            companies,
            reason: freeN ? null : 'free_used',
          }),
        3600,
      );
      return;
    }
    void (async () => {
      const alive = () => mountedRef.current && teaserRunRef.current === runId;
      try {
        const r = await fetch(PRISM_PREVIEW_URL, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prismRunId: runId }),
        });
        if (!r.ok || !alive()) return;
        let cur = ((await r.json()) as { preview?: PreviewView | null }).preview ?? null;
        setPreview(cur);
        for (let i = 0; cur?.status === 'running' && i < TEASER_POLL_MAX; i++) {
          await new Promise((res) =>
            setTimeout(res, i < TEASER_POLL_FAST ? TEASER_POLL_MS : TEASER_POLL_SLOW_MS),
          );
          if (!alive()) return;
          try {
            const g = await fetch(`${PRISM_PREVIEW_URL}/${encodeURIComponent(runId)}`, {
              credentials: 'include',
            });
            if (!g.ok) continue;
            cur = ((await g.json()) as { preview?: PreviewView | null }).preview ?? null;
            if (alive()) setPreview(cur);
          } catch {
            // Netz-Schluckauf: weiter nachfragen
          }
        }
        // Noch immer „läuft“: nicht „keine“ behaupten, sondern ehrlich „dauert länger“. Beim
        // Wiederkommen fragt der Funnel neu (der Server schließt Hängende selbst ab).
        if (cur?.status === 'running' && alive()) setPreview({ ...cur, stalled: true });
      } catch {
        // Komfort-Funktion: der Funnel läuft ohne Vorschau weiter wie bisher.
      }
    })();
  }, [showTeaserFlow, demo, engine]);
  const previewOn = !!preview && preview.status !== 'skipped';
  const previewDone = !!preview && preview.status === 'done';
  const previewByDomain = new Map(
    (preview?.companies ?? []).map((c) => [normDomain(c.domain), c] as const),
  );
  const previewFor = (ld: Lead) => previewByDomain.get(normDomain(ld.domain || ld.co)) ?? null;
  const freeLeads = (preview?.companies ?? []).filter(
    (c) => c.kind === 'free' && c.status === 'found' && !!c.person,
  );
  const freeMode = previewOn && (preview?.freeTarget ?? 0) > 0;
  // Ohne Gratis-Kontingent: die stärkste Firma bekommt den maskierten Entscheider (Teaser-Karte).
  const teaserCo = (preview?.companies ?? []).find((c) => c.kind === 'teaser') ?? null;
  const teaserView: TeaserView | null =
    previewOn && !freeMode
      ? teaserCo
        ? {
            status: teaserCo.status,
            domain: teaserCo.domain,
            companyName: teaserCo.companyName,
            person: teaserCo.person ?? null,
          }
        : preview?.status === 'running'
          ? { status: 'running', domain: freeLead.domain || '', companyName: freeLead.co }
          : previewDone
            ? {
                status: preview?.stalled ? 'failed' : 'none',
                domain: freeLead.domain || '',
                companyName: freeLead.co,
              }
            : null
      : null;
  // Gratis-Leads: gezeigt / keine (einmal je Lauf), für die Kauf-Quote danach je userId.
  useEffect(() => {
    if (!freeMode || !previewDone) return;
    const key = `${teaserRunRef.current}:free`;
    if (teaserEventRef.current === key) return;
    teaserEventRef.current = key;
    if (freeLeads.length > 0) {
      pushDataLayer('prism_free_leads_shown', { count: freeLeads.length });
      if (!demo)
        sendPrismEvent(
          'prism_free_leads_shown',
          prismRunIdRef.current,
          undefined,
          undefined,
          undefined,
          freeLeads.length,
        );
    } else {
      pushDataLayer('prism_free_leads_none', {});
      track('prism_free_leads_none', undefined, 'none');
    }
  });
  useEffect(() => {
    if (!previewOn || freeMode || preview?.reason !== 'free_used') return;
    const key = `${teaserRunRef.current}:used`;
    if (teaserEventRef.current === key) return;
    teaserEventRef.current = key;
    track('prism_free_leads_none', undefined, 'used');
  });
  const leadByDomain = (domain: string): Lead | undefined =>
    leadSet.find((l) => normDomain(l.domain || l.co) === normDomain(domain));
  const copyText = (text: string, kind: 'email' | 'phone' | 'all') => {
    try {
      void navigator.clipboard?.writeText(text);
    } catch {
      // Kopieren ist Komfort
    }
    setCopied(`${kind}:${text}`);
    schedule(() => setCopied(null), 1600);
    pushDataLayer('prism_free_lead_copied', { kind });
    track('prism_free_lead_copied', undefined, kind);
  };
  const exportFreeLeads = () => {
    const esc = (v: string | null | undefined) => `"${(v ?? '').replace(/"/g, '""')}"`;
    const head = [
      t('prism.free.csv.company'),
      t('prism.free.csv.domain'),
      t('prism.free.csv.name'),
      t('prism.free.csv.role'),
      t('prism.free.csv.email'),
      t('prism.free.csv.phone'),
      t('prism.free.csv.source'),
    ];
    const rows = freeLeads.map((c) => [
      c.companyName,
      c.domain,
      c.person?.name ?? '',
      c.person?.role ?? '',
      c.person?.email ?? '',
      c.person?.phone ?? '',
      c.person ? t(`prism.teaser.src.${c.person.source}`, { domain: c.domain }) : '',
    ]);
    const csv = `\ufeff${[head, ...rows].map((r) => r.map(esc).join(';')).join('\r\n')}`;
    try {
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leadscraper-gratis-leads.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      schedule(() => URL.revokeObjectURL(url), 2000);
    } catch {
      // Export ist Komfort
    }
    pushDataLayer('prism_free_leads_exported', { count: freeLeads.length });
    track('prism_free_leads_exported');
  };
  useEffect(() => {
    if (!teaserView || teaserView.status === 'running' || teaserView.status === 'skipped') return;
    const key = `${teaserRunRef.current}:${teaserView.status}`;
    if (teaserEventRef.current === key) return;
    teaserEventRef.current = key;
    if (teaserView.status === 'found') {
      pushDataLayer('prism_dm_teaser_shown', { email: teaserView.person?.emailStatus ?? 'none' });
      track('prism_dm_teaser_shown', undefined, teaserView.person?.emailStatus ?? 'none');
    } else {
      pushDataLayer('prism_dm_teaser_none', { status: teaserView.status });
      track('prism_dm_teaser_none', undefined, teaserView.status);
    }
  });
  const toggleTeaser = () => {
    const next = !teaserOpen;
    setTeaserOpen(next);
    if (next && !teaserOpenedRef.current) {
      teaserOpenedRef.current = true;
      pushDataLayer('prism_dm_teaser_opened', { status: teaserView?.status ?? '' });
      track('prism_dm_teaser_opened');
    }
  };
  // Klick auf eine Firmenkarte (nicht auf den Ansprechpartner-Knopf): die stärkste Firma klappt
  // ihren Teaser auf, jede andere zeigt einen Hinweis, was nach dem Freischalten kommt.
  const onCardClick = (e: React.MouseEvent, ld: Lead) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    const key = ld.domain || ld.co;
    if (previewFor(ld)?.kind === 'free') {
      teaserBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (teaserView && normDomain(key) === normDomain(freeLead.domain || freeLead.co)) {
      if (!teaserOpen) toggleTeaser();
      teaserBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setHintFor((cur) => (cur === key ? null : key));
    if (!hintSeenRef.current.has(key)) {
      hintSeenRef.current.add(key);
      pushDataLayer('prism_card_hint_opened', {});
      track('prism_card_hint_opened');
    }
  };
  // Was wir für eine Firma schon haben (Zahl + Rollen der Entscheider, E-Mail-Muster, Belege),
  // Namen verdeckt. Nur echte Daten aus der Vorschau; ohne Daten bleibt die Zeile wie bisher.
  const patternLabel = (pat: string) =>
    pat
      .replace('{first}', `{${t('prism.pattern.first')}}`)
      .replace('{last}', `{${t('prism.pattern.last')}}`)
      .replace('{f}', `{${t('prism.pattern.f')}}`);
  const rowHint = (ld: Lead) => {
    const pv = previewFor(ld);
    if (!pv) return null;
    if (pv.kind === 'free' && pv.status === 'found') {
      return <div className="wu-row-hint is-free">✓ {t('prism.hint.freeAbove')}</div>;
    }
    if (pv.kind !== 'hint' || pv.status !== 'found') return null;
    const dms = pv.decisionMakers ?? 0;
    return (
      <div className="wu-row-hint">
        {dms > 0 ? (
          <span>
            🔒{' '}
            {pv.roles?.length
              ? t(dms === 1 ? 'prism.hint.dmsOne' : 'prism.hint.dmsMany', {
                  count: dms,
                  roles: pv.roles.join(', '),
                })
              : t('prism.hint.dmsNoRole', { count: dms })}
          </span>
        ) : null}
        {pv.emailPattern ? (
          <span>
            {t('prism.hint.pattern')} <code>{patternLabel(pv.emailPattern)}</code>
          </span>
        ) : null}
        {pv.sources?.length ? (
          <span>
            {t('prism.teaser.source')}:{' '}
            {pv.sources.map((src) => t(`prism.hint.src.${src}`)).join(', ')}
          </span>
        ) : null}
      </div>
    );
  };
  const hasHintData = (ld: Lead) => {
    const pv = previewFor(ld);
    return !!pv && pv.kind === 'hint' && pv.status === 'found' && (pv.decisionMakers ?? 0) > 0;
  };
  const unlockFromHint = (ld: Lead) => {
    if (hasHintData(ld)) {
      pushDataLayer('prism_unlock_hint_clicked', {});
      track('prism_unlock_hint_clicked', 'lead');
    }
    unlockFromResults('lead');
  };
  const cardHint = (ld: Lead) =>
    hintFor === (ld.domain || ld.co) ? (
      <div className="wu-card-hint" aria-live="polite">
        <span>
          🔒 {t('prism.cardHint.body', { company: ld.co })}
          {teaserView?.status === 'found'
            ? ` ${t('prism.cardHint.example', { top: freeLead.co })}`
            : ''}
        </span>
        <button
          type="button"
          className="wu-card-hint-cta"
          onClick={() => unlockFromResults('lead')}
        >
          {t('prism.cardHint.cta')} <ArrowIcon size={13} />
        </button>
      </div>
    ) : null;
  const restartFunnel = () => {
    setAnalyze(null);
    analyzeRef.current = null;
    analyzeFiredRef.current = false;
    analyzedQueryRef.current = null; // F6: frischer Start = kein alter Analyze-Kontext
    searchFiredRef.current = false;
    setEngine(null);
    setEngineErr(null);
    engineRef.current = null;
    setQStep(0);
    qaRef.current = [];
    setCount(0);
    setDoneSteps([]);
    setRunSalt(Math.random().toString(36).slice(2)); // neuer Lauf → frische Kennzahlen
    // Review LEA-430: wie applySuggestion → die alte Run-Id fallen lassen, sonst
    // trägt der frische target-Snapshot noch die prismRunId des verworfenen Laufs
    // (und ein späteres ?resumeRun=<alteId> würde fälschlich als „schon
    // restauriert" durchgewinkt).
    prismRunIdRef.current = null;
    setPhase('target');
    phaseRef.current = 'target';
    // LEA-430 #2: bewusster Neustart → gespiegelten Stand verwerfen (der Save-
    // Effekt legt danach sofort einen frischen target-Snapshot an). Nur im echten
    // Kunden-Pfad — /dev/prism-demo darf den realen Funnel-Snapshot im selben Tab
    // nicht wegräumen (Restore/Save sind ebenfalls demo-gated).
    if (!demo) clearFunnelSnapshot();
  };
  // F6 (Stale-ICP): Der Funnel erlaubt Zurück-Navigation bis zum Ziel-Feld —
  // wer dort seinen Text ÄNDERT und weitergeht, lief bisher stillschweigend auf
  // dem alten /analyze weiter (alte Fragen, altes ICP, Suche auf altem Kontext).
  // Vor jedem Übergang target→website deshalb: geänderter Text → alles
  // invalidieren, was am alten Lauf hängt (frisches /analyze + neues Run-Doc);
  // unveränderter Text → Cache behalten, keine erneute Wartezeit.
  const invalidateStaleAnalyze = (nextTarget: string) => {
    if (analyzedQueryRef.current === null) return; // noch kein /analyze gefeuert
    if (analyzedQueryRef.current === nextTarget.trim()) return; // unverändert → Cache ok
    setAnalyze(null);
    analyzeRef.current = null;
    analyzeFiredRef.current = false;
    analyzedQueryRef.current = null;
    searchFiredRef.current = false;
    prismRunIdRef.current = null; // neuer Zieltext = neues Blackbox-Doc
    setEngine(null);
    engineRef.current = null;
    setEngineErr(null);
    engineErrRef.current = null;
    setQStep(0);
    qaRef.current = [];
  };
  // Refine-Vorschlags-Chip (Janik 03.07.): 1 Klick = neue Suche mit der
  // konkreten Alternativ-Zielgruppe — kein Zurück zum leeren Textfeld. Der
  // eigentliche runSearch feuert erst NACH dem State-Update (Effect auf
  // runSalt), damit query/metrics im frischen Closure stehen und die
  // Zahlen-Story konsistent bleibt (Zähler-Ziel = Headline-Zahl).
  const pendingRerunRef = useRef(false);
  const applySuggestion = (s: string) => {
    setQuery(s);
    setEngine(null);
    engineRef.current = null;
    setEngineErr(null);
    engineErrRef.current = null;
    searchFiredRef.current = false;
    prismRunIdRef.current = null; // neuer Lauf = neues Blackbox-Doc, kein Overwrite
    setCount(0);
    setDoneSteps([]);
    pendingRerunRef.current = true;
    setRunSalt(Math.random().toString(36).slice(2));
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusst nur runSalt — der Effect feuert genau einmal pro applySuggestion und liest dann die frischen Closures.
  useEffect(() => {
    if (!pendingRerunRef.current) return;
    pendingRerunRef.current = false;
    runSearch();
  }, [runSalt]);
  const audienceQuote = query.trim() ? `»${query.trim()}«` : t('prism.audienceFallback');
  // #4 (Quickwins): dieselbe Zielgruppe auch in den FETTESTEN Zeilen (Ergebnis-
  // Headline + Paywall-H1) — „1.809 Firmen zu »Steuerberater in Bayern« live
  // geprüft" beweist in der meistgelesenen Zeile, dass die KI verstanden hat.
  // Immer als »Zitat«-Einschub (wie audienceQuote), NIE grammatisch flektiert
  // (LEA-379 i18n#2). Gate: nur wenn kurz genug für H1/Mobile und offensichtlich
  // echter Text — sonst generischer Key, die fetteste Zeile darf nie zum
  // „KI-hat-nicht-verstanden"-Beweis kippen.
  const audienceHeadline = (() => {
    const q = query.trim();
    if (!q || q.length > 28) return null;
    const letters = (q.match(/\p{L}/gu) || []).length;
    const dense = q.replace(/\s/gu, '').length;
    if (letters < 3 || dense === 0 || letters / dense < 0.5) return null;
    return `»${q}«`;
  })();
  // Website-Verständnis (Janik 03.07.): zeigt der Nutzer eine Website, füllt die
  // Wartezeit der Suche die „dein Unternehmen verstanden"-Karte (Chancen +
  // Warum-jetzt) — löst nebenbei das tote-Wartezeit-Problem aus Baustelle ②.
  // #1 (Welle 2): risks aus dem Gate raus, momentum rein; risks bleibt im Typ
  // (Alt-Läufe), wird aber nirgends mehr gerendert.
  const icpView = analyze?.icp && !analyze.icp.__error ? analyze.icp : null;
  const showUnderstood =
    !!icpView &&
    (!!icpView.business_model || !!icpView.opportunities?.length || !!icpView.momentum);
  // Suche mit Verständnis-Karte: ab 900 px zweispaltig (Zähler + Schritte links, „Dein Unternehmen
  // verstanden“ rechts), damit die Wartezeit auf einem Bildschirm passt. Nur die Klasse; das
  // Layout entscheidet prismStage.css.
  const splitSearch = phase === 'searching' && showUnderstood && !!icpView;
  const qNum = qStep + 1;
  const qTotal = getQuestionCount();
  const qObj = getQuestionAt(qStep);
  const qText = qObj.q;
  // <2 Optionen = kein sinnvolles Chip-Set → Ja/Nein-Fallback rendert.
  const qOptions = qObj.options.length >= 2 ? qObj.options : null;

  const warmCls =
    phase === 'paywall'
      ? 'wu-overlay wu-hidden'
      : phase === 'results' && !rateLimited && !engineWeak && !searchFailed
        ? 'wu-overlay wu-wide'
        : 'wu-overlay';
  const paywallCls = phase === 'paywall' ? 'lea-pricing-overlay' : 'lea-pricing-overlay wu-hidden';

  // Fixe Kauf-Leiste: nur Paywall-Phase, kein Kauf-CTA sichtbar, Checkout zu.
  const showStickyBar = phase === 'paywall' && !plan && ctaOffscreen;

  const vmodalCls = videoOpen ? 'vmodal open' : 'vmodal';
  const coCls = plan ? 'co-overlay open' : 'co-overlay';
  const showPay = !!plan && !paid;
  const showSuccess = !!plan && paid;

  // Handler-Aliasse (renderVals-Namen)
  const onQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    // First keystroke in the target field — separates "engaged then abandoned"
    // from "landed & bounced" (both invisible to the server pre-/analyze).
    if (!targetTypedRef.current && e.target.value.trim()) {
      targetTypedRef.current = true;
      pushDataLayer('prism_target_typed', {});
    }
    setQuery(e.target.value);
    if (targetError) setTargetError(null);
  };
  const onWebsite = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebsite(e.target.value);
    if (websiteError) setWebsiteError(null);
    if (websiteIssue) setWebsiteIssue(null);
  };
  // Vor der Analyse: Website ist optional, aber wenn eine da ist, muss sie
  // strukturell eine Domain sein (instant, ohne Crawl). Sonst freundlicher Stopp.
  // 16.10.2026: dazu Adressen, die nicht die eigene Firmen-Website sind (leadscraper.de,
  // KI-Chat, Social-Profil, Weiterleitung) — 8 von 48 Läufen trugen so etwas ein.
  const startAnalyze = () => {
    const w = website.trim();
    if (w && !isPlausibleDomain(w)) {
      setWebsiteError(t('prism.website.errInvalid'));
      return;
    }
    const issue = w ? classifyPrismWebsite(w) : null;
    if (issue) {
      setWebsiteError(null);
      setWebsiteIssue(issue);
      pushDataLayer('prism_website_hint', { kind: issue });
      track('prism_website_hint', undefined, issue);
      return;
    }
    setWebsiteError(null);
    setWebsiteIssue(null);
    pushDataLayer('prism_website_submitted', { hasWebsite: !!w });
    runAnalyze();
  };
  // „Ohne Website weiter“: die Suche läuft dann nur mit der Zielgruppe (wie leeres Feld).
  const skipWebsite = () => {
    setWebsite('');
    setWebsiteError(null);
    setWebsiteIssue(null);
    pushDataLayer('prism_website_submitted', { hasWebsite: false, skipped: true });
    runAnalyze();
  };
  // Fail-fast: erst leichtgewichtig validieren, ob die Eingabe überhaupt eine
  // Zielgruppe ist. Bullshit → Premium-Fehler, kein Weiterkommen. Server aus →
  // fail-open (nicht blockieren, das End-Gate fängt es).
  const goWebsite = async () => {
    // `trimmed` statt `t` — würde sonst das i18n-t() shadowen.
    const trimmed = query.trim();
    if (!trimmed || validating) return;
    pushDataLayer('prism_target_submitted', { via: 'input' });
    setTargetError(null);
    setTargetNotice(null);
    setValidating(true);
    try {
      const r = await fetch(PRISM_VALIDATE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: trimmed, lang: LangStore.get() }),
      });
      const v: { valid?: boolean; reason?: string; suggestion?: string; notice?: string | null } =
        r.ok ? await r.json() : { valid: true };
      if (v.valid === false) {
        // A concrete, currently-silent pre-/analyze drop-off cause.
        pushDataLayer('prism_target_validation_failed', { reason: v.reason || '' });
        setTargetError(v.reason || t('prism.target.errFallback'));
        setTargetSuggestion(v.suggestion || '');
        setValidating(false);
        return;
      }
      // Gültig, aber dünne Datenlage (lokales B2C, außerhalb DACH): ehrlich sagen, nicht sperren.
      const notice =
        v.notice === 'local_b2c' || v.notice === 'outside_dach' ? (v.notice as TargetNotice) : null;
      setTargetNotice(notice);
      if (notice) {
        pushDataLayer('prism_target_notice', { kind: notice });
        track('prism_target_notice', undefined, notice);
      }
    } catch {
      // Validator nicht erreichbar → durchlassen.
    }
    setValidating(false);
    invalidateStaleAnalyze(trimmed); // F6: geänderter Zieltext → alter Lauf weg
    setPhase('website');
  };
  const chipSteuer = () => {
    const s = t('prism.target.chipQuery.steuer');
    setQuery(s);
    invalidateStaleAnalyze(s);
    setTargetNotice(null);
    setPhase('website');
  };
  const chipAgentur = () => {
    const s = t('prism.target.chipQuery.agentur');
    setQuery(s);
    invalidateStaleAnalyze(s);
    setTargetNotice(null);
    setPhase('website');
  };
  const chipHandwerk = () => {
    const s = t('prism.target.chipQuery.handwerk');
    setQuery(s);
    invalidateStaleAnalyze(s);
    setTargetNotice(null);
    setPhase('website');
  };
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const openVideo = () => {
    // Lazy-Load: iframe-Quelle erst beim ersten Öffnen setzen.
    const f = frameRef.current;
    if (f && !f.src.includes('/dub/v/')) f.src = DUB_VIDEO_BASE + DUB_VIDEO_DE;
    setVideoOpen(true);
  };
  const closeVideo = () => {
    // Playback stoppen: src auf about:blank zurück (analog OnboardingGate).
    const f = frameRef.current;
    if (f) f.src = 'about:blank';
    setVideoOpen(false);
  };
  const onModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) setVideoOpen(false);
  };
  // Der zweite Klick eines Doppelklicks landet schon im gerade geöffneten Bezahlfenster: auf
  // dem Hintergrund (Kauf-Knopf unterhalb der Karte) oder, seit die Ansprechpartner-Pillen das
  // Fenster öffnen, auf dem Schließen-Kreuz bzw. dem Bezahl-Knopf darüber. Schließen und
  // Bezahlen zählen deshalb erst einen kurzen Moment nach dem Öffnen.
  const coJustOpened = () => Date.now() - coOpenedAtRef.current < 500;
  const closeCheckout = () => {
    if (coJustOpened()) return;
    setPlan(null);
  };
  const onCoClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (coJustOpened()) return;
    setPlan(null);
  };
  const openTripwire = () => openCheckout({ name: '50 Leads', price: 49 });
  // Kauf-Knopf der Ergebnisliste (08.10.2026): öffnet das Bezahlfenster direkt. Vorher führte
  // er auf die lange Angebotsseite, deren Knopf erst das Bezahlfenster öffnete; von fünf
  // Klicks kam dort keiner weiter. Preis und Folgepreis (prism.results.foot) stehen am
  // Desktop unter dem Knopf, am Handy fest über dem fest stehenden Knopf, und noch einmal im
  // Bezahlfenster; die Angebotsseite bleibt über den Link darunter erreichbar.
  // `from: 'lead'` = Klick auf einen gesperrten Ansprechpartner in der Liste. Auch das ist die
  // Absicht „freischalten“; bisher passierte dort nichts.
  const unlockFromResults = (from: 'results' | 'lead' = 'results') => {
    // Der Knopf behält hinter dem offenen Fenster den Fokus: Enter/Leertaste nicht doppelt zählen.
    if (plan) return;
    pushDataLayer('prism_unlock_clicked', { from });
    openCheckout({ name: '50 Leads', price: 49 }, from);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    // `pw-takeover` fixes the root to the viewport (position: fixed; inset: 0).
    // The OnboardingGate mounts PrismShell as a sibling AFTER the dashboard, and
    // — unlike the standard pricing layouts, which wrap their content in a fixed
    // `.lea-pricing-overlay` — the Prism funnel body lives directly in
    // `.lea-pricing` (position: relative). Without pw-takeover the root flows in
    // normal document order BELOW the dashboard, leaving the dashboard visible
    // above it (Janik 05.07.: "beim Bezahlen auf der letzten Prism-Seite kommt
    // das Dashboard"). Fixed inset-0 makes it the viewport-covering takeover.
    <div className="lea-pricing pw-takeover">
      {/* A2 (LEA-379): invisible Turnstile mount point — usePrismTurnstile renders
          the widget here on demand. Hidden; dark until VITE_TURNSTILE_ENABLED. */}
      {turnstileEnabled && <div id={PRISM_TURNSTILE_CONTAINER_ID} style={{ display: 'none' }} />}
      {/* Sprach-Umschalter — fixed oben rechts, in ALLEN Phasen sichtbar
          (Funnel + Paywall), gleiche Ecke/Optik wie auf der Register-Seite. */}
      <div className="pw-lang">
        <AuthLangSwitch />
        {/* Re-present läuft: dezenter, sprachneutraler Spinner direkt am Schalter,
            wo der User gerade geklickt hat — die Ergebnistexte werden neu betextet. */}
        {representing && (
          <svg
            className="pw-lang-spin"
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            style={{ animation: 'lea-spin .8s linear infinite', opacity: 0.6 }}
          >
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
        )}
      </div>
      {/* ── Pre-Paywall Setup-Funnel (Warm-Up-Overlay) ───────────────────── */}
      <div className={warmCls}>
        <div className={`wu-card${splitSearch ? ' pz-card-split' : ''}`}>
          <div className="wu-kicker">
            <span className="pz-kicker-ctx">
              <span className="k-dot">
                <CheckIcon />
              </span>
              <span>
                {t('prism.kicker.account')}: <b>{t('prism.kicker.waiting')}</b>
              </span>
            </span>
            <PrismRail phase={phase} />
          </div>

          {/* Schritt 1 — Zielgruppe */}
          {phase === 'target' && (
            <div>
              {/* D2 (LEA-430 #3): „Willkommen zurück" — fertiger, ungekaufter Lauf
                  liegt auf dem Server. Explizites Angebot statt Auto-Jump. */}
              {resumeOffer && (
                <div className="wu-resume">
                  <div className="wu-resume-txt">
                    <strong>{t('prism.resume.title')}</strong>{' '}
                    {t('prism.resume.body', {
                      audience: resumeOffer.target?.trim()
                        ? `»${resumeOffer.target.trim()}«`
                        : t('prism.audienceFallback'),
                    })}
                  </div>
                  <div className="wu-resume-actions">
                    <button type="button" className="wu-resume-cta" onClick={acceptResume}>
                      {t('prism.resume.cta')}
                      <ArrowIcon />
                    </button>
                    <button type="button" className="wu-resume-dismiss" onClick={dismissResume}>
                      {t('prism.resume.dismiss')}
                    </button>
                  </div>
                </div>
              )}
              <div className="wu-head">
                <h1 className="wu-title">
                  {t('prism.target.title')}{' '}
                  <span className="grad">{t('prism.target.titleGrad')}</span>
                </h1>
                <p className="wu-sub">{t('prism.target.sub')}</p>
              </div>
              <div className="wu-body">
                <div className="wu-field">
                  <span className="wu-label">{t('prism.target.label')}</span>
                  <input
                    className="wu-input"
                    type="text"
                    value={query}
                    onChange={onQuery}
                    placeholder={t('prism.target.placeholder')}
                  />
                </div>
                <div className="wu-chips">
                  <button type="button" className="wu-chip" onClick={chipSteuer}>
                    {t('prism.target.chip.steuer')}
                  </button>
                  <button type="button" className="wu-chip" onClick={chipAgentur}>
                    {t('prism.target.chip.agentur')}
                  </button>
                  <button type="button" className="wu-chip" onClick={chipHandwerk}>
                    {t('prism.target.chip.handwerk')}
                  </button>
                </div>
                <div className="wu-target-tip">{t('prism.target.tip')}</div>
                {targetError && (
                  <div className="wu-verr" role="alert">
                    <span className="wu-verr-ic">
                      <svg
                        aria-hidden="true"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                      </svg>
                    </span>
                    <div className="wu-verr-body">
                      <strong>{targetError}</strong>
                      {targetSuggestion ? (
                        <button
                          type="button"
                          className="wu-verr-sug"
                          onClick={() => {
                            setQuery(targetSuggestion);
                            setTargetError(null);
                          }}
                        >
                          {t('prism.target.suggestion', { suggestion: targetSuggestion })}
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="wu-cta"
                  onClick={goWebsite}
                  disabled={validating}
                  aria-busy={validating}
                  style={validating ? { opacity: 0.75, cursor: 'progress' } : undefined}
                >
                  {validating ? (
                    <>
                      <span className="wu-btn-spin" aria-hidden="true" />
                      {t('prism.target.validating')}
                    </>
                  ) : (
                    <>
                      {t('prism.target.cta')} <ArrowIcon />
                    </>
                  )}
                </button>
                <div className="wu-foot">{t('prism.target.foot')}</div>
              </div>
            </div>
          )}

          {/* Schritt 2 — Website */}
          {phase === 'website' && (
            <div>
              {/* Zurück zum Ziel-Feld, falls die Zielgruppe korrigiert werden soll
                  (query bleibt als controlled input erhalten). */}
              <button type="button" className="wu-back" onClick={() => setPhase('target')}>
                {t('prism.questions.back')}
              </button>
              <div className="wu-head">
                <h1 className="wu-title">
                  {t('prism.website.title')}{' '}
                  <span className="grad">{t('prism.website.titleGrad')}</span>.
                </h1>
                <p className="wu-sub">{t('prism.website.sub')}</p>
              </div>
              {targetNotice && (
                <div className="pz-notice" role="note">
                  <strong>{t('prism.notice.title')}:</strong> {t(`prism.notice.${targetNotice}`)}{' '}
                  {t('prism.notice.continue')}
                </div>
              )}
              <div className="wu-body">
                <div className="wu-field">
                  <span className="wu-label">{t('prism.website.label')}</span>
                  <input
                    className="wu-input"
                    type="url"
                    value={website}
                    onChange={onWebsite}
                    placeholder={t('prism.website.placeholder')}
                  />
                </div>
                <div className="wu-hint">
                  <svg
                    aria-hidden="true"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                  </svg>
                  <span>{t('prism.website.hint')}</span>
                </div>
                {websiteError && (
                  <div className="wu-verr" role="alert">
                    <span className="wu-verr-ic">
                      <svg
                        aria-hidden="true"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                      </svg>
                    </span>
                    <div className="wu-verr-body">
                      <strong>{websiteError}</strong>
                    </div>
                  </div>
                )}
                {websiteIssue && (
                  <div className="wu-verr wu-verr-soft" role="alert">
                    <div className="wu-verr-body">
                      <strong>{t(`prism.website.issue.${websiteIssue}`)}</strong>
                    </div>
                  </div>
                )}
                <button type="button" className="wu-cta" onClick={startAnalyze}>
                  {t('prism.website.cta')} <ArrowIcon />
                </button>
                <button type="button" className="wu-skip" onClick={skipWebsite}>
                  {t('prism.website.skip')}
                </button>
                <div className="wu-foot">{t('prism.website.foot')}</div>
              </div>
            </div>
          )}

          {/* Analyzing — die KI „liest“ die Seite: Adressleiste mit der echten Domain, ein Lichtstrahl
              tastet Zeilen ab. Rein dekorativ, keine erfundenen Inhalte; die Schritte darunter
              kommen weiter aus dem echten Ablauf (StepList). */}
          {phase === 'analyzing' && (
            <div className="pz-analyze">
              <div className="pz-scan" aria-hidden="true">
                <div className="pz-scan-bar">
                  <span className="pz-scan-dots">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="pz-scan-url">
                    <svg
                      aria-hidden="true"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {website.trim() ? (
                        <>
                          <circle cx="12" cy="12" r="9" />
                          <path d="M3 12h18" />
                          <path d="M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
                        </>
                      ) : (
                        <>
                          <circle cx="11" cy="11" r="7" />
                          <path d="M21 21l-4.3-4.3" />
                        </>
                      )}
                    </svg>
                    <span>{readingLabel(website, query)}</span>
                  </span>
                </div>
                <div className="pz-scan-page">
                  <i className="h" />
                  <i className="b" />
                  <i />
                  <i />
                  <i />
                  <i />
                  <span className="pz-scan-beam" />
                </div>
              </div>
              <h1 className="pz-analyze-title">{t('prism.analyzing.reading')}</h1>
              <StepList doneSteps={doneSteps} loadMsg={loadMsg} />
            </div>
          )}

          {/* Schritt 3 — Fragen */}
          {phase === 'questions' && (
            <div>
              {/* Zurück-Affordance (Janik: Fehlklick-Korrektur ohne Neustart) —
                  Frage 2/3 eine Frage zurück, Frage 1 zurück zum Ziel-Feld. */}
              <button type="button" className="wu-back" onClick={goBackQuestion}>
                {t('prism.questions.back')}
              </button>
              <div className="wu-head" style={{ paddingBottom: '2px' }}>
                {/* Kein „Schritt X von 3“ mehr: den Fortschritt zeigt die Leiste in der Kopfzeile,
                    die Überschrift zählt nur die Fragen (Janik-Feedback: zwei Zähler verwirren). */}
                <h1 className="wu-title">
                  {t('prism.questions.title')}{' '}
                  <span className="grad">
                    {t('prism.questions.titleGrad', { num: qNum, total: qTotal })}
                  </span>
                  .
                </h1>
              </div>
              <div className="wu-body">
                <div className="wu-qtext">{qText}</div>
                {/* Antwort-Chips (Janik 03.07.): die Optionen beantworten die
                    Frage wörtlich — Entscheidung auf einen Blick statt
                    Ja/Nein-Interpretation. Ziffern 1-3 als Shortcuts. Fragen
                    ohne Optionen (String-Fallback) behalten die Ja/Nein-Karten. */}
                {qOptions ? (
                  <div
                    className={`${qOptions.length === 3 ? 'wu-cards wu-cards-3' : 'wu-cards'}${
                      qDir === -1 ? ' is-back' : ''
                    }`}
                    key={`opts-${qStep}`}
                  >
                    {qOptions.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`wu-card-opt wu-chip-opt${picked === opt ? ' is-picked' : ''}`}
                        onClick={() => answerQuestion(opt)}
                      >
                        <span className="kbd">{oi + 1}</span>
                        <span className="lb">{opt}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={`wu-cards${qDir === -1 ? ' is-back' : ''}`}>
                    <button
                      type="button"
                      className={`wu-card-opt yes${
                        picked === t('prism.questions.yes') ? ' is-picked' : ''
                      }`}
                      onClick={() => answerQuestion(t('prism.questions.yes'))}
                    >
                      <span className="kbd">J</span>
                      <span className="ic">
                        <CheckIcon size={20} sw={2.5} />
                      </span>
                      <span className="lb">{t('prism.questions.yes')}</span>
                    </button>
                    <button
                      type="button"
                      className={`wu-card-opt no${
                        picked === t('prism.questions.no') ? ' is-picked' : ''
                      }`}
                      onClick={() => answerQuestion(t('prism.questions.no'))}
                    >
                      <span className="kbd">N</span>
                      <span className="ic">
                        <svg
                          aria-hidden="true"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </span>
                      <span className="lb">{t('prism.questions.no')}</span>
                    </button>
                  </div>
                )}
                <div className="wu-foot">{t('prism.questions.foot')}</div>
              </div>
            </div>
          )}

          {/* Searching */}
          {phase === 'searching' && (
            <div className={`wu-searching${splitSearch ? ' pz-search-split' : ''}`}>
              <div className="pz-search-main">
                <div className="wu-radar">
                  <span className="sweep" />
                  <span className="blip b1" />
                  <span className="blip b2" />
                  <span className="blip b3" />
                </div>
                {/* #3 (Welle 2): die Such-Phase zeigt NUR den animierten Zähler —
                  nie die echte kleine Zahl (das war der sichtbare Sprung). Der
                  Morph auf kept passiert erst beim Reveal in der Ergebnis-Phase.
                  key={count}: Remount bei jedem Increment triggert den Zähl-Puls. */}
                <div className="wu-count" key={count}>
                  {fmtInt(count)}
                </div>
                <div className="wu-count-l">{t('prism.searching.label')}</div>
                {/* A5-9: determinater Fortschritts-Faden (erledigte / gesamt Steps). */}
                <div className="wu-progress" aria-hidden="true">
                  <span
                    className="wu-progress-fill"
                    style={{
                      width: `${Math.min(100, Math.round((doneSteps.length / Math.max(1, searchStepTotal)) * 100))}%`,
                    }}
                  />
                </div>
                <StepList doneSteps={doneSteps} loadMsg={loadMsg} maxDone={3} />
              </div>
              {/* Unternehmens-Verständnis-Karte (Janik 03.07.): füllt die
                  Such-Wartezeit mit dem Beweis „wir haben dein Geschäft
                  verstanden". #1 (Welle 2): statt Chancen/„Zu beachten"-Genörgel
                  jetzt 3 konkrete Vertriebschancen (volle Breite) + eine
                  hervorgehobene „Warum jetzt"-Zeile — streng website-gegroundet.
                  Defensiv: momentum optional (Alt-Läufe → Panel fällt weg). */}
              {showUnderstood && icpView && (
                <div className="wu-understood">
                  <div className="wu-understood-h">
                    <span className="wu-understood-tick" aria-hidden="true">
                      <CheckIcon size={11} sw={2.5} />
                    </span>
                    {t('prism.understood.title')}
                  </div>
                  {icpView.business_model && (
                    <div className="wu-understood-model">{icpView.business_model}</div>
                  )}
                  {!!icpView.opportunities?.length && (
                    <ul className="wu-understood-opps">
                      {icpView.opportunities.slice(0, 3).map((o) => (
                        <li key={o}>
                          <span className="wu-understood-opp-t">{o}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {icpView.momentum && (
                    <div className="wu-understood-momentum">
                      <span className="wu-understood-mo-ic" aria-hidden="true">
                        <BoltIcon size={11} />
                      </span>
                      <span>
                        <b>{t('prism.understood.momentumLabel')}</b> {icpView.momentum}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* F9: Tages-Kontingent erreicht — ehrlicher Zustand OHNE Vorschlags-
              Chips (die würden nur den nächsten 429 erzeugen) und ohne
              „präzisiere deine Zielgruppe"-Unterton: das Limit liegt bei uns,
              nicht an der Eingabe des Nutzers. */}
          {phase === 'results' && rateLimited && (
            <div className="wu-results">
              <div className="wu-found">
                <div className="n">{t('prism.rateLimit.title')}</div>
                <p>
                  {t('prism.rateLimit.body')}
                  {rateLimitResetMs ? (
                    <>
                      <br />
                      <span style={{ opacity: 0.7, fontSize: 13 }}>
                        {t('prism.rateLimit.reset', {
                          time: new Date(rateLimitResetMs).toLocaleTimeString(lang, {
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        })}
                      </span>
                    </>
                  ) : null}
                </p>
              </div>
              {/* Review-Finding 12.07.: ohne CTA wäre der Screen eine Sackgasse
                  im Escape-gesperrten Onboarding-Gate. restartFunnel cleart
                  engineErr → rateLimited=false → zurück zum Ziel-Screen. */}
              <button
                type="button"
                className="wu-cta"
                style={{ marginTop: 20 }}
                onClick={restartFunnel}
              >
                {t('prism.rateLimit.cta')}
                <ArrowIcon />
              </button>
            </div>
          )}
          {/* Results (Aha) */}
          {phase === 'results' && !rateLimited && (engineWeak || searchFailed) && (
            <div className="wu-results">
              <div className="wu-found">
                <div className="n">{t('prism.refine.title')}</div>
                <p>
                  {t('prism.refine.body', { audience: audienceQuote })}
                  {/* engineReason kommt (noch) hartkodiert deutsch aus der Engine
                      (engine.ts reasonText). Bis Janiks Lokalisierung deployed ist,
                      NUR bei deutscher UI zeigen — sonst leakt Deutsch in EN/FR/…-Funnel.
                      Der lokalisierte prism.refine.body oben trägt die Aussage bereits. */}
                  {lang === 'de' && engineReason ? (
                    <>
                      <br />
                      <span style={{ opacity: 0.7, fontSize: 13 }}>{engineReason}</span>
                    </>
                  ) : null}
                </p>
              </div>
              {/* Klickbare Alternativ-Zielgruppen (Janik 03.07.): Refine ist
                  keine Sackgasse — 1 Klick startet die neue Suche direkt. */}
              {!!engine?.quality?.suggestions?.length && (
                <div className="wu-refine-chips">
                  <span className="wu-refine-chips-l">{t('prism.refine.try')}</span>
                  {engine.quality.suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="wu-refine-chip"
                      onClick={() => applySuggestion(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div className="wu-hint" style={{ marginTop: 16 }}>
                <span>
                  {t('prism.refine.hint1')} <strong>{t('prism.refine.hintStrong')}</strong>{' '}
                  {t('prism.refine.hint2')}
                </span>
              </div>
              <button
                type="button"
                className="wu-cta"
                style={{ marginTop: 20 }}
                onClick={restartFunnel}
              >
                {t('prism.refine.cta')}
                <ArrowIcon />
              </button>
            </div>
          )}
          {phase === 'results' && !engineWeak && !searchFailed && (
            <div className="wu-results wu-results-wide">
              <div className="wu-found">
                <div className="n">
                  {/* #6 (Welle 2): echte kept-Zahl trägt die Headline; der Wert
                      morpht beim Reveal sichtbar von rawFound → kept (#3).
                      #4 (Quickwins): Zielgruppe als »Zitat« in der Headline,
                      wenn sie das Gate besteht — sonst generisch. */}
                  <span className="grad">{headlineFmt}</span>{' '}
                  {audienceHeadline
                    ? t('prism.results.foundSuffixAud', { audience: audienceHeadline })
                    : t('prism.results.foundSuffix')}
                </div>
                {/* #13 (Janik 10.07.): NIE „nur X passen" — das macht die Engine klein
                    und die 50er-Freischaltung unlogisch. Story = Fülle + Auswahl:
                    großer Markt → live geprüft → hier die stärksten → 50 gibt's beim
                    Freischalten (Seed + Fresh-Fill liefern real Richtung 50). */}
                <p>
                  <strong>{t('prism.results.topLine')}</strong> {t('prism.results.unlockHint')}{' '}
                  <strong>{t('prism.results.unlockOffer', leadVars)}</strong>.
                </p>
                {/* Markt-Hochrechnung nur zeigen, wenn die Engine eine echte,
                    bereits als Schätzung markierte Spanne geliefert hat (#6/#11). */}
                {marketRange ? (
                  <p style={{ marginTop: 6, fontSize: 13, opacity: 0.7 }}>
                    {t('prism.results.disclaimer')}
                  </p>
                ) : null}
              </div>
              {/* #11 (Welle 2): 3 ECHTE Stat-Tiles aus dem Lauf statt synthetischer
                  Kennzahlen (metrics.ap/revenueM). Stufe 1 kept · Stufe 2 onTarget ·
                  Ø Fit-Score. Alle Charts/Insights/Timing-Blöcke mit hardcodierten
                  bzw. kalenderbasierten Fantasiewerten (Firmengröße 31/46/18/5 %,
                  Datenqualität 96/88/92 %, 61 % Öffnungsrate, Di–Do, 4.200 €,
                  Wettbewerb, +18 % Entscheider, Saisonalitäts-Chart) STRICHEN —
                  strukturell nicht aus echten Lauf-Daten befüllbar (UWG/Ehrlichkeit).
                  „Lieber weniger Kacheln als erfundene." */}
              {/* #13: ohne echte Engine-Daten (Demo-Mock/Fallback) keine Kacheln —
                  eine einzelne „—"-Kachel wirkt kaputt statt premium. */}
              {kept != null && (
                <div className="wu-stats">
                  <div className="wu-stat">
                    <div className="v">{keptFmt}</div>
                    <div className="l">{t('prism.results.stat.checked')}</div>
                  </div>
                  {/* #13: Markt-Fülle statt „X passen" — echte Engine-Schätzung
                    (market.projected_range_dach); ohne Schätzung entfällt die Kachel. */}
                  {marketTileValue ? (
                    <div className="wu-stat">
                      <div className="v">{marketTileValue}</div>
                      <div className="l">{t('prism.results.stat.market')}</div>
                    </div>
                  ) : null}
                  {avgFit != null && (
                    <div className="wu-stat">
                      <div className="v">{Math.round(avgFit)}</div>
                      <div className="l">{t('prism.results.stat.avgFit')}</div>
                    </div>
                  )}
                </div>
              )}
              {/* So haben wir gesucht (29.09.2026): die echten Suchbegriffe der Engine. Zeigt die
                  Arbeit hinter dem Ergebnis und macht sichtbar, wie die Zielgruppe verstanden
                  wurde — wer eine Stadt oder ein Gewerk vermisst, sieht es hier sofort. */}
              {searchQueries.length > 0 && (
                <div className="wu-queries">
                  <span className="wu-queries-l">{t('prism.results.queriesLabel')}</span>
                  {searchQueries.slice(0, 8).map((q) => (
                    <span className="wu-query" key={q}>
                      {q}
                    </span>
                  ))}
                  {searchQueries.length > 8 && (
                    <span className="wu-query wu-query-more">
                      {t('prism.results.queriesMore', { count: searchQueries.length - 8 })}
                    </span>
                  )}
                </div>
              )}
              {targetNotice && (
                <div className="pz-notice pz-notice-results" role="note">
                  <strong>{t('prism.notice.title')}:</strong> {t(`prism.notice.${targetNotice}`)}
                </div>
              )}
              <div className="wu-freelead" ref={teaserBlockRef}>
                <div className="wfl-head">
                  {/* 16.10.2026: Für die stärkste Firma recherchieren wir den Entscheider schon vor
                    dem Kauf (teaserView). Ohne Teaser (Lauf < 2 Firmen, Kill-Switch, alte API)
                    bleibt der Block wie bisher. */}
                  <span className="wfl-tag">
                    <GiftIcon />{' '}
                    {freeMode
                      ? // Fix 18.10.: die Überschrift nennt, was wirklich da ist — nie „3“, wenn
                        // die Recherche weniger oder keinen Gratis-Lead gefunden hat.
                        !previewDone
                        ? preview?.stalled
                          ? t('prism.free.tagSlow')
                          : t('prism.free.tagRunning', { n: preview?.freeTarget ?? 3 })
                        : freeLeads.length === 0
                          ? t('prism.free.tagNone')
                          : freeLeads.length === 1
                            ? t('prism.free.tagOne')
                            : t('prism.free.tag', { n: freeLeads.length })
                      : teaserView
                        ? t('prism.teaser.tag')
                        : freeLead.email
                          ? t('prism.freelead.tag')
                          : t('prism.freelead.tagTop')}
                  </span>
                  {!teaserView && !freeMode && freeLead.email ? (
                    <span className="wfl-verified">
                      <svg
                        aria-hidden="true"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>{' '}
                      {t('prism.freelead.verified')}
                    </span>
                  ) : null}
                </div>
                {freeMode ? (
                  <div className="wfl-free">
                    {freeLeads.map((c) => {
                      const ld = leadByDomain(c.domain) ?? {
                        in: initials(c.companyName),
                        co: c.companyName,
                        meta: '',
                        name: '',
                        role: '',
                        ap: 0,
                        domain: c.domain,
                      };
                      const p = c.person;
                      if (!p) return null;
                      return (
                        <div className="wfl-free-card" key={c.domain}>
                          <div className="wfl-free-top">
                            <LeadFavicon lead={ld} size={40} />
                            <div className="wfl-main">
                              <div className="wfl-co">
                                {c.companyName} <ScoreBadge lead={ld} />
                              </div>
                              <div className="wfl-free-person">
                                <b>{p.name}</b>
                                {p.role ? <span className="wfl-role"> · {p.role}</span> : null}
                              </div>
                            </div>
                          </div>
                          <div className="wfl-free-data">
                            {p.email ? (
                              <button
                                type="button"
                                className="wfl-free-chip"
                                onClick={() => copyText(p.email ?? '', 'email')}
                                title={t('prism.free.copy')}
                              >
                                <span className="wfl-free-val">{p.email}</span>
                                <span className="wfl-free-act">
                                  {copied === `email:${p.email}`
                                    ? t('prism.free.copied')
                                    : t('prism.free.copy')}
                                </span>
                              </button>
                            ) : null}
                            {p.phone ? (
                              <button
                                type="button"
                                className="wfl-free-chip"
                                onClick={() => copyText(p.phone ?? '', 'phone')}
                              >
                                <span className="wfl-free-val">{p.phone}</span>
                                <span className="wfl-free-act">
                                  {copied === `phone:${p.phone}`
                                    ? t('prism.free.copied')
                                    : t('prism.free.copy')}
                                </span>
                              </button>
                            ) : null}
                          </div>
                          <div className="wfl-free-meta">
                            <span className={`wfl-dm-badge wfl-dm-badge-${p.emailStatus}`}>
                              {t(`prism.teaser.mail.${p.emailStatus}`)}
                            </span>{' '}
                            <span className="wfl-free-src">
                              {t('prism.teaser.source')}:{' '}
                              {p.sourceUrl ? (
                                <a href={p.sourceUrl} target="_blank" rel="noopener noreferrer">
                                  {t(`prism.teaser.src.${p.source}`, { domain: c.domain })}
                                </a>
                              ) : (
                                t(`prism.teaser.src.${p.source}`, { domain: c.domain })
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {preview?.status === 'running' ? (
                      <div className="wfl-free-running">
                        <span className="wu-btn-spin wfl-dm-spin" aria-hidden="true" />
                        {preview.stalled
                          ? t('prism.free.slowBody')
                          : t('prism.free.running', {
                              found: freeLeads.length,
                              n: preview?.freeTarget ?? 3,
                            })}
                      </div>
                    ) : null}
                    {previewDone && freeLeads.length === 0 ? (
                      <p className="wfl-dm-note">{t('prism.free.noneBody')}</p>
                    ) : null}
                    {previewDone &&
                    freeLeads.length > 0 &&
                    freeLeads.length < (preview?.freeTarget ?? 0) ? (
                      <p className="wfl-dm-note">
                        {t('prism.free.fewer', {
                          found: freeLeads.length,
                          n: preview?.freeTarget ?? 3,
                        })}
                      </p>
                    ) : null}
                    {freeLeads.length > 0 ? (
                      <div className="wfl-free-foot">
                        <span>{t('prism.free.yours')}</span>
                        <button type="button" className="wfl-free-export" onClick={exportFreeLeads}>
                          {t('prism.free.export')}
                        </button>
                      </div>
                    ) : null}
                    {previewDone ? (
                      <button
                        type="button"
                        className="wfl-dm-cta"
                        onClick={() => unlockFromResults('lead')}
                      >
                        {t('prism.free.cta', leadVars)} <ArrowIcon size={14} />
                      </button>
                    ) : null}
                  </div>
                ) : teaserView ? (
                  <div className={`wfl-card wfl-teaser${teaserOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="wfl-toggle"
                      aria-expanded={teaserOpen}
                      onClick={toggleTeaser}
                    >
                      <LeadFavicon lead={freeLead} size={44} />
                      <span className="wfl-main">
                        <span className="wfl-co">
                          {freeLead.co} <ScoreBadge lead={freeLead} top />
                        </span>
                        <span className={`wfl-dm wfl-dm-${teaserView.status}`}>
                          {teaserView.status === 'running' ? (
                            <>
                              <span className="wu-btn-spin wfl-dm-spin" aria-hidden="true" />
                              {t('prism.teaser.running', { company: freeLead.co })}
                            </>
                          ) : teaserView.status === 'found' && teaserView.person ? (
                            <>
                              <CheckIcon size={12} sw={3} />{' '}
                              {t('prism.teaser.foundShort', { name: teaserView.person.name })}
                              {teaserView.person.role ? ` · ${teaserView.person.role}` : ''}
                            </>
                          ) : teaserView.status === 'none' ? (
                            t('prism.teaser.noneShort')
                          ) : (
                            t('prism.teaser.failedShort')
                          )}
                        </span>
                      </span>
                      <span className="wfl-chev">
                        {teaserOpen ? t('prism.teaser.close') : t('prism.teaser.open')}
                        <ChevronIcon open={teaserOpen} />
                      </span>
                    </button>
                    {teaserOpen && (
                      <div className="wfl-dm-body">
                        {teaserView.status === 'found' && teaserView.person ? (
                          <>
                            <div className="wfl-dm-person">
                              <b>{teaserView.person.name}</b>
                              {teaserView.person.role ? (
                                <span className="wfl-role"> · {teaserView.person.role}</span>
                              ) : null}
                            </div>
                            <div className="wfl-dm-row">
                              <span className="k">{t('prism.teaser.source')}</span>
                              <span className="v">
                                {teaserView.person.sourceUrl ? (
                                  <a
                                    href={teaserView.person.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {t(`prism.teaser.src.${teaserView.person.source}`, {
                                      domain: teaserView.domain || freeLead.domain || freeLead.co,
                                    })}
                                  </a>
                                ) : (
                                  t(`prism.teaser.src.${teaserView.person.source}`, {
                                    domain: teaserView.domain || freeLead.domain || freeLead.co,
                                  })
                                )}
                              </span>
                            </div>
                            <div className="wfl-dm-row">
                              <span className="k">{t('prism.teaser.email')}</span>
                              <span className="v">
                                {teaserView.person.emailMasked ? (
                                  <>
                                    <code className="wfl-dm-mail">
                                      {teaserView.person.emailMasked}
                                    </code>{' '}
                                    <span
                                      className={`wfl-dm-badge wfl-dm-badge-${teaserView.person.emailStatus}`}
                                    >
                                      {t(`prism.teaser.mail.${teaserView.person.emailStatus}`)}
                                    </span>
                                  </>
                                ) : (
                                  t('prism.teaser.mail.none')
                                )}
                              </span>
                            </div>
                            <p className="wfl-dm-note">{t('prism.teaser.foundNote', leadVars)}</p>
                          </>
                        ) : teaserView.status === 'none' ? (
                          <>
                            <p className="wfl-dm-note">
                              {t('prism.teaser.noneBody', { company: freeLead.co })}
                            </p>
                            <p className="wfl-dm-note">{t('prism.teaser.noneNote')}</p>
                          </>
                        ) : teaserView.status === 'failed' ? (
                          <p className="wfl-dm-note">{t('prism.teaser.failedBody', leadVars)}</p>
                        ) : (
                          <p className="wfl-dm-note">
                            {t('prism.teaser.running', { company: freeLead.co })}
                          </p>
                        )}
                        {preview?.reason === 'free_used' ? (
                          <p className="wfl-dm-note">{t('prism.free.usedNote')}</p>
                        ) : null}
                        {freeLead.fitReason ? (
                          <div className="wfl-dm-fit">{freeLead.fitReason}</div>
                        ) : null}
                        <LeadEvidence text={freeLead.evidence} />
                        <button
                          type="button"
                          className="wfl-dm-cta"
                          onClick={() => unlockFromResults('lead')}
                        >
                          {t('prism.teaser.cta', leadVars)} <ArrowIcon size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="wfl-card">
                      <LeadFavicon lead={freeLead} size={44} />
                      <div className="wfl-main">
                        <div className="wfl-co">
                          {freeLead.co} <ScoreBadge lead={freeLead} top />
                        </div>
                        <div className="wfl-meta">{freeLead.meta}</div>
                      </div>
                      {freeLead.email ? (
                        <div className="wfl-contact">
                          <div className="wfl-person">
                            {freeLead.name} <span className="wfl-role">· {freeLead.role}</span>
                          </div>
                          <div className="wfl-data">
                            <span className="wfl-chip">
                              <svg
                                aria-hidden="true"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect width="20" height="16" x="2" y="4" rx="2" />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                              </svg>
                              {freeLead.email}
                            </span>
                            <span className="wfl-chip">
                              <svg
                                aria-hidden="true"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                              {freeLead.phone}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="wfl-contact">
                          {freeLead.fitReason ? (
                            <div className="wfl-person" style={{ fontWeight: 500 }}>
                              {freeLead.fitReason}
                            </div>
                          ) : null}
                          <LeadEvidence text={freeLead.evidence} />
                          <div className="wfl-data">
                            <button
                              type="button"
                              className="wfl-chip wfl-chip-btn"
                              onClick={() => unlockFromResults('lead')}
                            >
                              {t('prism.freelead.lockedChip')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="wfl-note">
                      {freeLead.email ? (
                        <>
                          {t('prism.freelead.note1')}{' '}
                          <strong>{t('prism.freelead.noteStrong')}</strong>{' '}
                          {t('prism.freelead.note2', leadVars)}
                        </>
                      ) : (
                        <>
                          {t('prism.freelead.alt1')}{' '}
                          <strong>{t('prism.freelead.altStrong')}</strong>
                          {sp(t('prism.freelead.alt2', leadVars))}
                          {t('prism.freelead.alt2', leadVars)}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="wu-listcard">
                <div className="wu-leadlist">
                  {resultLeads.map((ld) => (
                    <React.Fragment key={ld.co}>
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Komfort für Maus/Touch (Clarity: tote Klicks auf die Karte); per Tastatur führt der Ansprechpartner-Knopf in der Zeile zum selben Ziel. */}
                      <div
                        className={`wu-lead wu-lead-click${hintFor === (ld.domain || ld.co) ? ' is-hint' : ''}`}
                        onClick={(e) => onCardClick(e, ld)}
                      >
                        <LeadFavicon lead={ld} size={36} />
                        <div className="li">
                          <div className="co">
                            {ld.co} <ScoreBadge lead={ld} />
                          </div>
                          {/* Echte Fit-Begründung statt Adresse (Mock behält Meta). */}
                          <div className="ro">
                            {ld.fitReason ? shortReason(ld.fitReason) : ld.meta}
                          </div>
                          <LeadEvidence text={ld.evidence} />
                          {rowHint(ld)}
                        </div>
                        <button
                          type="button"
                          className="wu-ap wu-ap-btn"
                          onClick={() => unlockFromHint(ld)}
                        >
                          <ApStack seed={ld.domain || ld.co} />
                          {hasHintData(ld)
                            ? `${t('prism.hint.unlock')} ${t('prism.hint.unlockSub')}`
                            : t('prism.lead.ap')}
                          <span className="wu-ap-x">
                            {hasHintData(ld) ? ' 🔒' : ` · ${t('prism.lead.inclDeciders')} 🔒`}
                          </span>
                        </button>
                      </div>
                      {cardHint(ld)}
                    </React.Fragment>
                  ))}
                </div>
                {/* Janik 10.07.: Fülle zum ANFASSEN — der geprüfte Pool existiert ja
                  (poolCompanies), also zeigen wir echte, benannte Firmen als gesperrte
                  Zeilen statt nur einer "+X weitere"-Behauptung. Gleicher Baustein wie
                  die Vault-Wand (#5); ohne Pool (Demo/Alt-Lauf) entfällt der Block. */}
                {wallLeads.length > 0 && (
                  <div className="wu-results-wall">
                    {wallLeads.slice(0, 12).map((ld) => (
                      // Janik 11.07.: EXAKT derselbe Zeilen-Baustein wie die Paywall-
                      // Wand (vault-row-wall) — vorher „vault-row-compact" mit nackten
                      // Initialen + einsamem Schloss (nie gestylte Klassen) → sah
                      // „anders/unfertig" aus. Jetzt Logo + Name/Badge + AP-Teaser,
                      // konsistent mit den Karten darüber und der Paywall danach.
                      <React.Fragment key={ld.domain || ld.co}>
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: wie oben, der Knopf in der Zeile ist der Tastatur-Weg. */}
                        <div
                          className={`vault-row vault-row-wall wu-lead-click${hintFor === (ld.domain || ld.co) ? ' is-hint' : ''}`}
                          onClick={(e) => onCardClick(e, ld)}
                        >
                          <LeadFavicon lead={ld} size={36} />
                          <div className="vault-co">
                            <div className="vc-name">
                              <span className="vc-name-text">{ld.co}</span>
                              <ScoreBadge lead={ld} />
                            </div>
                            <div className="vc-meta">
                              {ld.fitReason ? shortReason(ld.fitReason) : ld.meta}
                            </div>
                            {rowHint(ld)}
                          </div>
                          <button
                            type="button"
                            className="vault-ap vault-ap-btn"
                            onClick={() => unlockFromHint(ld)}
                          >
                            <span className="vap-l1">
                              <ApStack seed={ld.domain || ld.co} />
                              {hasHintData(ld)
                                ? `${t('prism.hint.unlock')} ${t('prism.hint.unlockSub')}`
                                : t('prism.lead.ap')}
                            </span>
                            <span className="vap-l2">
                              {hasHintData(ld) ? '' : t('prism.lead.inclDeciders')} 🔒
                            </span>
                          </button>
                        </div>
                        {cardHint(ld)}
                      </React.Fragment>
                    ))}
                    <div className="wu-results-wall-fade" aria-hidden="true" />
                  </div>
                )}
              </div>
              <div className="wu-more">
                + <b>{t('prism.results.moreStrong', { count: fmtInt(moreCount) })}</b>
                {sp(t('prism.results.moreRest'))}
                {t('prism.results.moreRest')}
              </div>
              <div className="wu-plan">
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                <div>
                  <b>{t('prism.results.planTitle')}</b> {t('prism.results.planBody', leadVars)}
                </div>
              </div>
              <div className="wu-buybar">
                <button
                  type="button"
                  className="wu-cta wu-cta-sticky"
                  onClick={() => unlockFromResults()}
                >
                  {t('prism.results.cta', leadVars)} <ArrowIcon />
                </button>
                {/* 16.10.2026: Abo-Satz statt prism.results.foot (der Käufer vom 08.10. klappte
                    „Kann ich jederzeit kündigen?“ dreimal auf). */}
                <AboTerms cfg={cfg} className="wu-foot" />
                <button type="button" className="wu-details-link" onClick={enterPaywall}>
                  {t('prism.results.detailsLink')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Paywall ──────────────────────────────────────────────────────── */}
      <div className={paywallCls}>
        {/* has-sticky-bar reserviert unten Platz, damit die fixe Kauf-Leiste
            am Seitenende nicht die Gründer-/Support-Zeile verdeckt. */}
        <div className={showStickyBar ? 'shell shell-wide has-sticky-bar' : 'shell shell-wide'}>
          <div className="step-strip">
            <span className="item done">
              <span className="dot">
                <CheckIcon sw={2.5} />
              </span>
              {t('prism.kicker.account')}
            </span>
            <span className="sep" />
            <span className="item active">
              <span className="dot">2</span>
              {t('prism.paywall.step2')}
            </span>
            <span className="sep" />
            <span className="item">
              <span className="dot">3</span>
              {t('prism.paywall.step3')}
            </span>
          </div>

          {/* A resumed run is saved; nothing is reserved or counted down. */}
          {runResumed && (
            <div className="res-bar res-bar-saved">
              <CheckIcon />
              <span>{t('prism.resume.saved')}</span>
            </div>
          )}

          <div className="shell-header">
            <span className="eyebrow">
              <GiftIcon size={14} />
              {t('prism.paywall.eyebrow', { count: keptFmt })}
            </span>
            <h1 className="shell-title">
              {t('prism.paywall.title1')}{' '}
              <span className="grad">{t('prism.paywall.titleGrad', leadVars)}</span>{' '}
              {/* #4 (Quickwins): H1 trägt die Zielgruppe als »Zitat«, wenn das
                  Gate besteht — sonst der generische title2. */}
              {audienceHeadline
                ? t('prism.paywall.title2Aud', { audience: audienceHeadline })
                : t('prism.paywall.title2')}
            </h1>
            <p className="shell-subtitle">
              {t('prism.paywall.sub1')}{' '}
              <strong>{t('prism.paywall.subStrong1', { count: keptFmt })}</strong>{' '}
              {t('prism.paywall.sub2', { audience: audienceQuote })}{' '}
              <strong>{t('prism.paywall.subStrong2', leadVars)}</strong> {t('prism.paywall.sub3')}
            </p>
          </div>

          <div className="vault">
            <div className="vault-head">
              <div>
                <div className="vault-title">{t('prism.vault.title', leadVars)}</div>
                <div className="vault-sub">
                  {/* #6 (Welle 2): 3-Stufen-Vokabel aus echten Zahlen — kept live
                      geprüft → onTarget passen zur Zielgruppe → deine 50 stärksten.
                      Alle drei Keys mitgeben, damit die (noch alten) 6 Fremd-
                      sprachen sauber interpolieren, bis der i18n-Folge-Agent zieht. */}
                  {t('prism.vault.sub', {
                    ...leadVars,
                    count: keptFmt,
                    audience: audienceQuote,
                    onTarget: onTargetFmt,
                  })}
                </div>
              </div>
              <span className="vault-lock">
                <svg
                  aria-hidden="true"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {t('prism.vault.locked')}
              </span>
            </div>
            <div className="vault-list">
              {vaultLeads.map((ld) => (
                <div className="vault-row" key={ld.domain || ld.co}>
                  <LeadFavicon lead={ld} size={34} />
                  <div className="vault-co">
                    {/* #4 (Welle 2): Name in eigenes trunkierendes Kind, Badge als
                        flex-shrink-0-Geschwister — kein Ellipsis mitten in der Badge. */}
                    <div className="vc-name">
                      <span className="vc-name-text">{ld.co}</span>
                      <ScoreBadge lead={ld} />
                    </div>
                    {/* Echte Fit-Begründung der Engine (heißer als die Adresse);
                        Mock-Leads ohne fitReason behalten ihre Meta-Zeile. */}
                    <div className="vc-meta">
                      {ld.fitReason ? shortReason(ld.fitReason) : ld.meta}
                    </div>
                  </div>
                  <div className="vault-contact">
                    {ld.name ? (
                      <>
                        <div className="vc-person">
                          <span className="blurred">{ld.name}</span>
                          <span className="vc-role">{ld.role}</span>
                        </div>
                        <div className="vc-data">
                          <span className="blurred">{t('prism.vault.blurEmail')}</span>
                          <span className="blurred">+49 170 0000000</span>
                        </div>
                      </>
                    ) : ld.evidence ? (
                      <LeadEvidence text={ld.evidence} />
                    ) : (
                      <div className="vc-locked">{t('prism.freelead.lockedChip')}</div>
                    )}
                  </div>
                  {/* AP-Teaser statt nacktem „6 AP" — weiche Schätzung („~"),
                      keine erfundenen Namen; Entscheider recherchieren wir real. */}
                  <span className="vault-ap">
                    <span className="vap-l1">
                      <ApStack seed={ld.domain || ld.co} />
                      {t('prism.lead.ap')}
                    </span>
                    <span className="vap-l2">{t('prism.lead.inclDeciders')} 🔒</span>
                  </span>
                </div>
              ))}
              {/* #5 (Welle 2) + Konsistenz-Fix (Janik 10.07.: 'soll aussehen wie die
                  ersten oben'): die Wand aus dem vollen gescorten Pool (fit ≥ 40)
                  rendert jetzt im GLEICHEN Stil wie die Top-Zeilen — echtes Firmen-
                  Logo (LeadFavicon) + Score-Badge + Fit-Begründung + Ansprechpartner-
                  Teaser rechts. KEIN geblurrter Personen-Block wie oben, weil die
                  Wand-Leads vor dem Kauf keine Kontaktperson haben (nichts erfinden) —
                  bei dieser Breite ist die Personen-Spalte oben ohnehin ausgeblendet,
                  darum fluchten die Zeilen. Perf: content-visibility:auto + lazy
                  Favicons (LeadFavicon fällt bei Fehler auf Monogramm zurück).
                  Fehlt poolCompanies → leer. */}
              {wallLeads.map((ld) => (
                <div className="vault-row vault-row-wall" key={ld.domain || ld.co}>
                  <LeadFavicon lead={ld} size={34} />
                  <div className="vault-co">
                    <div className="vc-name">
                      <span className="vc-name-text">{ld.co}</span>
                      <ScoreBadge lead={ld} />
                    </div>
                    <div className="vc-meta">
                      {ld.fitReason ? shortReason(ld.fitReason) : ld.meta}
                    </div>
                  </div>
                  <span className="vault-ap">
                    <span className="vap-l1">
                      <ApStack seed={ld.domain || ld.co} />
                      {t('prism.lead.ap')}
                    </span>
                    <span className="vap-l2">{t('prism.lead.inclDeciders')} 🔒</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="vault-fade">
              {/* #6 (Welle 2): echte Restmenge des Pools statt statischem „+45";
                  ohne Pool (Demo/Alt-Lauf) gar nicht anzeigen statt „+0 weitere". */}
              {wallLeads.length > 0 ? (
                <span className="vault-more">
                  <svg
                    aria-hidden="true"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  {t('prism.vault.more', { count: wallLeads.length })}
                </span>
              ) : (
                <span />
              )}
              <div className="vault-cta-wrap">
                <button
                  type="button"
                  className="vault-cta"
                  onClick={openTripwire}
                  ref={vaultCtaRef}
                >
                  {t('prism.vault.cta', leadVars)} <ArrowIcon />
                </button>
                <AboTerms cfg={cfg} className="vault-reassure" />
              </div>
            </div>
          </div>

          <div className="hero-card">
            <div className="founder-side">
              <div className="fn-head">{t('prism.founder.head')}</div>
              <p className="fn-quote">{t('prism.founder.quote')}</p>
              <div className="fn-foot">
                <img className="fn-photo" alt="Janik Deimann" src={FOUNDER_PHOTO} />
                <div className="fn-sig">
                  <strong>Janik Deimann</strong>
                  <span>{t('prism.founder.role')}</span>
                </div>
              </div>
            </div>
            <div
              className="video-side demo-thumb"
              onClick={openVideo}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openVideo();
                }
              }}
              // biome-ignore lint/a11y/useSemanticElements: block-level Video-Thumbnail mit CSS-Layout (.demo-thumb) — nativer <button> würde das Grid brechen.
              role="button"
              tabIndex={0}
              aria-label={t('prism.video.playAria')}
            >
              <img className="demo-poster" src={DUB_VIDEO_POSTER} alt="" loading="lazy" />
              <span className="vchip">
                <span className="live-dot" /> {t('prism.video.chip')}
              </span>
              <span className="demo-play">
                <span className="pbtn">
                  <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="#1C1B1F">
                    <path d="M8 5.14v13.72c0 .8.87 1.3 1.56.88l11-6.86a1.04 1.04 0 0 0 0-1.76l-11-6.86A1.04 1.04 0 0 0 8 5.14z" />
                  </svg>
                </span>
              </span>
              <div className="video-overlay">
                <div className="vo-title">{t('prism.video.title')}</div>
                <div className="vo-cta">
                  <svg
                    aria-hidden="true"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5.14v13.72c0 .8.87 1.3 1.56.88l11-6.86a1.04 1.04 0 0 0 0-1.76l-11-6.86A1.04 1.04 0 0 0 8 5.14z" />
                  </svg>
                  {t('prism.video.cta')}
                </div>
              </div>
            </div>
          </div>

          <div className="howto">
            <div className="howto-label">{t('prism.timeline.title')}</div>
            <div className="timeline">
              <div className="tl-step">
                <span className="tl-when">
                  <span className="tl-dot">
                    <GiftIcon size={11} />
                  </span>
                  {t('prism.timeline.today')}
                </span>
                <div className="tl-body">
                  <strong>{t('prism.timeline.todayStrong')}</strong>
                  {sp(t('prism.timeline.todayRest', leadVars))}
                  {t('prism.timeline.todayRest', leadVars)}
                </div>
              </div>
              <div className="tl-step">
                <span className="tl-when">
                  <span className="tl-dot">
                    <BoltIcon size={11} />
                  </span>
                  {t('prism.timeline.days13')}
                </span>
                <div className="tl-body">
                  {t('prism.timeline.testP1')} <strong>{t('prism.timeline.testStrong')}</strong>{' '}
                  {t('prism.timeline.testP2')}
                </div>
              </div>
              <div className="tl-step">
                <span className="tl-when">
                  <span className="tl-dot">
                    <svg
                      aria-hidden="true"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <line x1="2" x2="22" y1="10" y2="10" />
                    </svg>
                  </span>
                  {t('prism.timeline.day4')}
                </span>
                <div className="tl-body">
                  {t('prism.timeline.aboP1')} <strong>{t('prism.timeline.aboStrong')}</strong>{' '}
                  {t('prism.timeline.aboP2')}
                </div>
              </div>
            </div>
          </div>

          <div className="versus">
            <div className="versus-head">{t('prism.versus.title')}</div>
            <div className="versus-cols">
              <div className="vs-col vs-old">
                <div className="vs-col-h">{t('prism.versus.oldTitle')}</div>
                <ul className="vs-list">
                  {[
                    t('prism.versus.old1'),
                    t('prism.versus.old2'),
                    t('prism.versus.old3'),
                    t('prism.versus.old4'),
                    t('prism.versus.old5'),
                  ].map((txt) => (
                    <li key={txt}>
                      <span className="vs-ic">
                        <svg
                          aria-hidden="true"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </span>
                      {txt}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="vs-col vs-new">
                <div className="vs-col-h">
                  <BoltIcon size={13} />
                  {t('prism.versus.newTitle')}
                </div>
                <ul className="vs-list">
                  {[
                    t('prism.versus.new1', leadVars),
                    t('prism.versus.new2'),
                    t('prism.versus.new3'),
                    t('prism.versus.new4'),
                    t('prism.versus.new5'),
                  ].map((txt) => (
                    <li key={txt}>
                      <span className="vs-ic">
                        <CheckIcon size={11} />
                      </span>
                      {txt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="versus-foot">
              {t('prism.versus.foot1')} <strong>{t('prism.versus.footStrong1')}</strong>{' '}
              {t('prism.versus.foot2')} <strong>{t('prism.versus.footStrong2')}</strong>{' '}
              {t('prism.versus.foot3')} <strong>{t('prism.versus.footStrong3')}</strong>.
            </div>
          </div>

          <div className="offer-grid">
            <div className="roi-panel">
              <div className="roi-head">
                <svg
                  aria-hidden="true"
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                  <polyline points="16 7 22 7 22 13" />
                </svg>
                {t('prism.roi.title', leadVars)}
              </div>
              <div className="roi-flow">
                <div className="roi-step">
                  <span className="rl">{t('prism.roi.row1')}</span>
                  <span className="rv">50</span>
                </div>
                {/* #12 (Janik 10.07.): '4.200 € Ø Auftragswert' war erfunden (wir
                    kennen den Deal-Wert des Users nicht) — ersetzt durch den ECHTEN,
                    vertraglichen Einsatz (49 €). Auch 'row3v' war nur 4200÷49 zurück-
                    gerechnet → generisch statt fiktiver Multiplikator (UWG). */}
                <div className="roi-step">
                  <span className="rl">{t('prism.roi.row2')}</span>
                  <span className="rv">49 €</span>
                </div>
                <div className="roi-step highlight">
                  <span className="rl">{t('prism.roi.row3')}</span>
                  <span className="rv">{t('prism.roi.row3v')}</span>
                </div>
              </div>
              <div className="roi-highlight">
                {t('prism.roi.hl1')} <strong>{t('prism.roi.hlStrong')}</strong>{' '}
                {t('prism.roi.hl2', leadVars)}
              </div>
              <div className="roi-stats">
                <div className="roi-stat">
                  <div className="v">&lt; 1 €</div>
                  <div className="l">{t('prism.roi.perLead')}</div>
                </div>
                <div className="roi-stat">
                  <div className="v">{t('prism.roi.stat2v')}</div>
                  <div className="l">{t('prism.roi.stat2')}</div>
                </div>
                <div className="roi-stat">
                  <div className="v">{t('prism.roi.stat3v')}</div>
                  <div className="l">{t('prism.roi.stat3')}</div>
                </div>
              </div>
            </div>
            <aside className="buy-side">
              <div className="plans plans-side" style={{ gridTemplateColumns: '1fr', margin: 0 }}>
                <div className="plan plan-recommended">
                  <div className="plan-badge">
                    <svg
                      aria-hidden="true"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    {t('prism.plan.badge')}
                  </div>
                  <div className="plan-leads">
                    <span className="plan-leads-num">{leadN}</span>
                    <span className="plan-leads-unit">{t('prism.plan.leadsUnit')}</span>
                  </div>
                  <div className="plan-price-row">
                    <div className="plan-anchor">
                      <svg
                        aria-hidden="true"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                      {t('prism.plan.anchor')} <s>≈ 350 €</s>
                    </div>
                    <div className="plan-price-main">
                      <span className="plan-price">49 €</span>
                      {/* Janik 11.07.: kein USt-Label am Haken/CTA — nur „49 €". Der echte
                          Endbetrag inkl. Steuer erscheint im Checkout, sobald der Kunde zahlt. */}
                      <span className="plan-price-suffix">{t('prism.plan.priceSuffix')}</span>
                    </div>
                    <div className="plan-perlead">
                      <strong>{t('prism.plan.perLeadStrong')}</strong> {t('prism.roi.perLead')}
                    </div>
                  </div>
                  <ul className="plan-features">
                    <li className="plan-feature">
                      <CheckIcon size={16} sw={2.5} />
                      <span>{t('prism.plan.f1')}</span>
                    </li>
                    <li className="plan-feature">
                      <CheckIcon size={16} sw={2.5} />
                      <span>{t('prism.plan.f2')}</span>
                    </li>
                    <li className="plan-feature">
                      <CheckIcon size={16} sw={2.5} />
                      <span>{t('prism.plan.f3')}</span>
                    </li>
                  </ul>
                  <div className="plan-terms">
                    <strong>{t('prism.plan.termsStrong')}</strong> {t('prism.plan.termsBody')}
                  </div>
                  <button
                    type="button"
                    className="plan-cta plan-cta-light"
                    onClick={openTripwire}
                    ref={planCtaRef}
                  >
                    {t('prism.plan.cta')} <ArrowIcon />
                  </button>
                  <AboTerms cfg={cfg} className="plan-abo" />
                  <div className="guarantee">
                    <span className="g-ic">
                      <svg
                        aria-hidden="true"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                    </span>
                    <div className="g-txt">
                      <strong>{t('prism.guarantee.title')}</strong>
                      <span className="g-sub">{t('prism.guarantee.sub')}</span>
                    </div>
                  </div>
                  <div className="cta-note">
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="18" height="11" x="3" y="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {t('prism.plan.secure')}
                  </div>
                  <div className="plan-subnote">
                    {t('prism.plan.subnote')}
                    <br />
                    {t('prism.plan.gbCondition')}{' '}
                    <a href={t('prism.legal.agbUrl')} target="_blank" rel="noopener noreferrer">
                      {t('prism.legal.agb')}
                    </a>
                    .
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <PrismReviewsBand />

          <div className="trust-bar" style={{ marginTop: '28px' }}>
            <div className="trust-item">
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect width="20" height="14" x="2" y="5" rx="2" />
                <line x1="2" x2="22" y1="10" y2="10" />
              </svg>
              <div>
                <strong>{t('prism.trust.payStrong')}</strong> {t('prism.trust.payRest')}
              </div>
            </div>
            <div className="trust-item">
              <ClockIcon size={16} />
              <div>
                <strong>{t('prism.trust.cancelStrong')}</strong> {t('prism.trust.cancelRest')}
              </div>
            </div>
            <div className="trust-item">
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <div>
                <strong>{t('prism.trust.dataStrong')}</strong> {t('prism.trust.dataRest')}
              </div>
            </div>
          </div>

          <div className="faq">
            <div className="faq-h">{t('prism.faq.title')}</div>
            {[
              { q: t('prism.faq.q1'), a: t('prism.faq.a1') },
              { q: t('prism.faq.q2'), a: t('prism.faq.a2') },
              { q: t('prism.faq.q3'), a: t('prism.faq.a3') },
              { q: t('prism.faq.q4'), a: t('prism.faq.a4') },
              { q: t('prism.faq.q5'), a: t('prism.faq.a5') },
            ].map((f) => (
              <details className="faq-i" key={f.q}>
                <summary>
                  {f.q}
                  <svg
                    aria-hidden="true"
                    className="chev"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </summary>
                <div className="faq-a">{f.a}</div>
              </details>
            ))}
          </div>

          <div className="founder-line">
            <div className="founder-left">
              <img className="founder-ava" alt="Janik Deimann" src={FOUNDER_PHOTO} />
              <div className="founder-copy">
                <strong>{t('prism.founderline.strong')}</strong> {t('prism.founderline.rest')}
                <div className="f-sub">{t('prism.founderline.sub')}</div>
              </div>
            </div>
            <a className="founder-ask" href="mailto:support@leadscraper.eu">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              {t('prism.founderline.ask')}
            </a>
          </div>

          {/* Fixe Kauf-Leiste (Desktop): erscheint erst, wenn beide Kauf-CTAs aus
              dem Viewport gescrollt sind; verschwindet, sobald das Checkout-
              Overlay offen ist. <640px ausgeblendet (CSS), dort übernehmen die
              bestehenden Mobile-Muster. */}
          {showStickyBar && (
            <div className="sticky-cta">
              <div className="sc-left">
                <span className="sc-price">
                  {t('prism.sticky.leads', leadVars)} <b>49 €</b> {t('prism.sticky.today')}
                </span>
              </div>
              <button
                type="button"
                className="sc-btn"
                onClick={() => openCheckout({ name: '50 Leads', price: 49 }, 'sticky')}
              >
                {t('prism.sticky.cta')} <ArrowIcon />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Inline-Checkout-Overlay ──────────────────────────────────────── */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Backdrop-Klick schließt das Overlay (Komfort); der reguläre Weg ist der ×-Button im Card. */}
      <div className={coCls} onClick={onCoClick}>
        <div className="co-card">
          <div className="co-head">
            <div>
              <div className="co-eyebrow">{t('prism.checkout.eyebrow')}</div>
              <h3>{t('prism.checkout.title', leadVars)}</h3>
            </div>
            <button
              type="button"
              className="co-close"
              onClick={closeCheckout}
              aria-label={t('prism.a11y.close')}
            >
              ×
            </button>
          </div>
          <div className="co-body">
            {showPay && (
              <div>
                <div className="co-summary">
                  <div className="co-sum-l">
                    <div className="a">{t('prism.checkout.sumPrice')}</div>
                    <div className="b">{t('prism.checkout.sumDesc', leadVars)}</div>
                  </div>
                  {/* Folgepreis klar und deutlich (AGB § 6 Abs. 5, Janik 29.09.): früher
                      bewusst dezent (08.07.) — jetzt eine normale Zeile mit Preis und
                      Hinweis auf die automatische Umwandlung. Volle Konditionen in co-fine. */}
                  <div className="co-terms">
                    <strong>
                      {t('prism.checkout.after')} · {t('prism.checkout.monthly')}
                    </strong>
                    <span>{t('prism.checkout.autoNote')}</span>
                  </div>
                </div>
                {/* Konsolidierte Reassurance (Janik 08.07.): früher DREI fast gleiche
                    Blöcke — co-reassure (grün) + co-invite (🎁) + co-guarantee (grün)
                    sagten alle „50 Leads zum SK, risikofrei". Jetzt EIN Kasten mit
                    zwei KLAR unterschiedlichen Zeilen (Kündigungs-Sicherheit vs.
                    Geld-zurück) + verschiedenen Icons, damit sie sich nicht doppeln.
                    Redirect-only-Checkout unverändert (LEA-379): CTA übergibt einmal
                    an Stripe, keine Karten-Doppelerfassung. */}
                <div className="co-assure">
                  {showDeliverNote && (
                    <div className="co-assure-row">
                      <svg
                        aria-hidden="true"
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                      </svg>
                      <span>{t('prism.checkout.deliverNote', { count: deliverable })}</span>
                    </div>
                  )}
                  <div className="co-assure-row">
                    <svg
                      aria-hidden="true"
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                    <span>{t('prism.checkout.reassure3d')}</span>
                  </div>
                  <div className="co-assure-row">
                    <svg
                      aria-hidden="true"
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                      <path d="M3 3v5h5" />
                    </svg>
                    <span>
                      <strong>{t('prism.guarantee.title')}</strong> {t('prism.guarantee.sub')}
                    </span>
                  </div>
                </div>
                {/* Stufe 4.3: inline Embedded Checkout NUR wenn opt-in (Flag) UND
                    kein Init-Fehler; sonst der bewährte Redirect-CTA. Default =
                    Redirect (Launch-Pfad). Demo behält Redirect/Fake-Pfad. */}
                {!demo && !elementFallback ? (
                  <PrismPaymentElement
                    prismRunId={prismRunIdRef.current ?? undefined}
                    leads={leadN}
                    onFallback={() => setElementFallback(true)}
                  />
                ) : !demo && EMBEDDED_CHECKOUT_ENABLED && !embedFallback ? (
                  <PrismEmbeddedCheckout
                    prismRunId={prismRunIdRef.current ?? undefined}
                    onFallback={() => setEmbedFallback(true)}
                  />
                ) : (
                  <button type="button" className="co-pay" disabled={paying} onClick={submitPay}>
                    {paying ? (
                      <>
                        <svg
                          aria-hidden="true"
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          style={{ animation: 'lea-spin .85s linear infinite' }}
                        >
                          <path d="M21 12a9 9 0 11-6.219-8.56" />
                        </svg>{' '}
                        {t('prism.checkout.paying')}
                      </>
                    ) : (
                      <>
                        <svg
                          aria-hidden="true"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect width="18" height="11" x="3" y="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>{' '}
                        {t('prism.checkout.payCta', leadVars)}
                      </>
                    )}
                  </button>
                )}
                {/* B (Janik 07.07.): direkt unterm CTA die „komm ich wieder raus?"-
                    Angst nehmen — Abo jederzeit mit 1 Klick kündbar. */}
                {/* 16.10.2026: der volle Abo-Satz direkt unter dem Bezahl-Knopf (statt nur
                    „Abo jederzeit mit 1 Klick kündbar“), dazu was danach kommt. */}
                <AboTerms cfg={cfg} className="co-cancel-note" />
                <div className="co-foot">
                  <svg
                    aria-hidden="true"
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>{' '}
                  {t('prism.checkout.securePrefix')} <span className="s">Stripe</span>{' '}
                  {t('prism.checkout.secureSuffix')}
                </div>
                <div className="co-fine">
                  {t('prism.checkout.fine1')}{' '}
                  <a href={t('prism.legal.agbUrl')} target="_blank" rel="noopener noreferrer">
                    {t('prism.legal.agb')}
                  </a>{' '}
                  &amp;{' '}
                  <a href={t('prism.legal.privacyUrl')} target="_blank" rel="noopener noreferrer">
                    {t('prism.legal.privacy')}
                  </a>{' '}
                  {t('prism.checkout.fine2')}
                </div>
              </div>
            )}
            {showSuccess && (
              <div className="co-success">
                <span className="ic">
                  <CheckIcon size={28} sw={2.5} />
                </span>
                <h4>{t('prism.success.title', leadVars)}</h4>
                <p>{t('prism.success.body', leadVars)}</p>
                <button type="button" className="ok" onClick={closeCheckout}>
                  {t('prism.success.cta')} <ArrowIcon size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Video-Modal (echtes Dub-Video, lazy-load wie die anderen Paywalls) ── */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Backdrop-Klick schließt das Video-Modal (Komfort); der reguläre Weg ist der ×-Button. */}
      <div className={vmodalCls} onClick={onModalClick}>
        <div className="vmodal-wrap">
          <button
            type="button"
            className="vmodal-close"
            onClick={closeVideo}
            aria-label={t('prism.a11y.close')}
          >
            ×
          </button>
          <div className="vmodal-box">
            <iframe
              ref={frameRef}
              className="vframe"
              loading="lazy"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              title={t('prism.video.iframeTitle')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrismShell;
